import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle} from 'react';
import ReactDOM from 'react-dom';
 import axios from 'axios';
 import GetCookie from '../../utils/GetCookie';
 import FabricSelector from '../../utils/FabricSelector';
 import ConfigureVM from './ConfigureVM';
 import AgentTranslationModal from './AgentTranslationModal';
import LanguageSelector from './LanguageSelector';
 import ConfirmationModal from '../../utils/ConfirmationModal';
import GenbiTemplateLibraryList from './GenbiTemplateLibraryList';
import ImportFromDocumentModal from './ImportFromDocumentModal';
import GenbiHeadingMappingModal from './GenbiHeadingMappingModal';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';
import AgentConfigure from './AgentConfigure';
import ReportFormattingModal, { ColorPickerButton } from "./ReportFormattingModal";
import GenbiResponseComponent from './GenbiResponseComponent';
import TableStructureSelector from './TableStructureSelector';

/**
 * Parses a raw XML-tag response string (from doc-generate flow) into the
 * shape that GenbiResponseComponent expects: { markdown, tables, images, ... }
 */
const parseRawResponse = (raw) => {
  if (!raw || typeof raw !== 'string') return null;

  const extract = (tag) => {
    const m = raw.match(new RegExp(`<${tag}>(.*?)</${tag}>`, 's'));
    return m ? m[1].trim() : '';
  };

  const markdown = extract('MARKDOWN');

  // Parse HTMLTABLES → tables array
  const tablesRaw = extract('HTMLTABLES');
  const tables = [];
  const tableRe = /<HTMLTABLE(\d+)(?:\s+SUBTITLE\s*=\s*"([^"]*)")?\s*>(.*?)<\/HTMLTABLE\1>/gs;
  let tm;
  while ((tm = tableRe.exec(tablesRaw)) !== null) {
    tables.push({
      table_id: `preview_table_${tm[1]}`,
      tableNumber: parseInt(tm[1]),
      subtitle: tm[2] || '',
      html: tm[3].trim(),
    });
  }

  // Parse IMAGES → images array
  const imagesRaw = extract('IMAGES');
  const images = [];
  const imageRe = /<IMAGE(\d+)(?:\s+SUBTITLE\s*=\s*"([^"]*)")?(?:\s+FOOTER\s*=\s*"([^"]*)")?\s*>(.*?)<\/IMAGE\1>/gs;
  let im;
  while ((im = imageRe.exec(imagesRaw)) !== null) {
    images.push({
      img_id: `preview_img_${im[1]}`,
      figureNumber: parseInt(im[1]),
      subtitle: im[2] || '',
      footer: im[3] || '',
      s3_path: null, // not resolved yet
    });
  }

  // Parse ABBREVIATIONS
  const abbrRaw = extract('ABBREVIATIONS');
  const abbreviations = [];
  const abbrRe = /<ABBR\d+\s+SHORT\s*=\s*"([^"]*)"\s+FULL\s*=\s*"([^"]*)"\s*\/>/g;
  let ab;
  while ((ab = abbrRe.exec(abbrRaw)) !== null) {
    abbreviations.push({ short: ab[1], full: ab[2] });
  }

  if (!markdown && tables.length === 0) return null;

  return { markdown, tables, images, graphs: [], abbreviations, references: null };
};

const DEFAULT_REPORT_CONFIG = {
  paragraph: [
    {
      id: "default",
      name: "Paragraph (Default)",
      fontFamily: "Arial",
      fontSize: 12,
      bold: false,
      italic: false,
      color: "#000000",
      alignment: "justify"
    },
    {
      id: "paragraph-1",
      name: "Paragraph (Style 1)",
      fontFamily: "Arial",
      fontSize: 12,
      bold: false,
      italic: false,
      color: "#000000",
      alignment: "justify",
      backgroundColor: "#D9D9D9",
      borders: "none",
      level: 1
    },
    {
      id: "paragraph-2",
      name: "Paragraph (Style 2)",
      fontFamily: "Arial",
      fontSize: 12,
      bold: false,
      italic: false,
      color: "#000000",
      alignment: "justify",
      backgroundColor: "#F2F2F2",
      borders: "none",
      level: 2
    }
  ],
  caption: {
    fontFamily: "Arial",
    fontSize: 12,
    bold: false,
    italic: false,
    color: "#000000"
  },
  headers: [
    {
      id: "default",
      name: "Header (Default)",
      fontFamily: "Arial",
      fontSize: 14,
      bold: true,
      italic: false,
      color: "#000000",
      level: 0
    },
    {
      id: "header-level-1",
      name: "Header (Level 1)",
      fontFamily: "Arial",
      fontSize: 16,
      bold: true,
      italic: false,
      color: "#000000",
      level: 1
    },

    {
      id: "header-level-2",
      name: "Header (Level 2)",
      fontFamily: "Arial",
      fontSize: 14,
      bold: true,
      italic: true,
      color: "#000000",
      level: 2
    }
  ],
  tables: {
    general: {
      text: {
        fontFamily: "Arial",
        fontSize: 11,
        bold: false,
        italic: false,
        color: "#000000"
      },
      header: {
        fontFamily: "Arial",
        fontSize: 11,
        bold: true,
        italic: false,
        color: "#000000",
        backgroundColor: null
      },
      design: "design-1",
      captionPlacement: "above"
    },
    toc: {
      text: {
        fontFamily: "Arial",
        fontSize: 12,
        bold: false,
        italic: false,
        color: "#000000"
      },
      design: "design-1"
    }
  },
  numberingFormat: "B",
  numberingStartValue: 1,
  citationFormat: {
    style: "nice",
    inline: {
      fontSize: 10,
      fontColor: "#000000",
      "superscript": true
    }
  },
  figureCaptionPlacement: "above",
  lineSpacing: 1.5,
  spaceAfter: 12
};

const COMPACT_MAX_SECTION_LEVEL = 1;

 const GenbiTemplateForm = forwardRef(({
   editingTemplate, 
   preloadedTemplate,
   fromSLR = false, 
   onSuccess, 
   onError, 
   onCancel,
   onDirtyChange,
   onProcessingStart, 
   onProcessingEnd,
   onTemplateCreated,
}, ref) => {
   const [formData, setFormData] = useState({
   template_name: '',
   fabric_id: null,
   fabric_name: '',
   sectionsDict: {},
   sectionOrder: [],
   vm_config: false,  
   lang_id:1,
   format_json: DEFAULT_REPORT_CONFIG,
   global_prompt: '',
   frontpage: null
 });
 
   const [isLoading, setIsLoading] = useState(false);
   const [fabricTreeData, setFabricTreeData] = useState([]);
   const [documentSelectorOpen, setDocumentSelectorOpen] = useState(false);
   const [currentAgentIndex, setCurrentAgentIndex] = useState({ sectionKey: null, agentKey: null });
   const [isInitialFabricLoad, setIsInitialFabricLoad] = useState(false);
 
   //magic prompt state variables
   const [magicPrompt, setMagicPrompt] = useState('');
   const [showMagicConfirm, setShowMagicConfirm] = useState(false);
   const [isMagicGenerating, setIsMagicGenerating] = useState(false);
   const [vmConfiguration, setVmConfiguration] = useState(null);
   const [useValueMessage, setUseValueMessage] = useState(false);
   const [valueMessageModalOpen, setValueMessageModalOpen] = useState(false);
  const [isLangModalOpen, setIsLangModalOpen] = useState(false);
  const [langsData, setLangsData] = useState({});
  const [isLoadingLangs, setIsLoadingLangs] = useState(false);
  // selectedLanguage: { id: number, language_name: string } — display only
  const [selectedLanguage, setSelectedLanguage] = useState({ id: 1, language_name: 'English' });

  const [isTemplateLibraryOpen, setIsTemplateLibraryOpen] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [templateToImport, setTemplateToImport] = useState(null);

  const [importedLibraryTemplateId, setImportedLibraryTemplateId] = useState(null);
  const [importedLibraryTemplateName, setImportedLibraryTemplateName] = useState('');
  const [isImportFromDocOpen, setIsImportFromDocOpen] = useState(false);

  const [headingMappingId, setHeadingMappingId] = useState(null);
  const [savedMapping, setSavedMapping] = useState(null);
  const [isLoadingSavedMapping, setIsLoadingSavedMapping] = useState(false);

  const [showFabricSwitchConfirm, setShowFabricSwitchConfirm] = useState(false);
  const [pendingFabricSwitch, setPendingFabricSwitch] = useState(null);

  const [agentConfigureOpen, setAgentConfigureOpen] = useState(false);
  const [showReportFormatting, setShowReportFormatting] = useState(false);
  const [summarySectionsExpanded, setSummarySectionsExpanded] = useState(false);

  const [blockingOverlayCount, setBlockingOverlayCount] = useState(0);
  const [agentToConfigure, setAgentToConfigure] = useState({ sectionKey: null, agentKey: null });
  const [activePromptTab, setActivePromptTab] = useState({});
  const [showResponsePanel, setShowResponsePanel] = useState({});
  // True only when sections were loaded via "Import from Document" generate flow
  const [isDocGeneratedImport, setIsDocGeneratedImport] = useState(false);
  const [showSpecialSectionInfo, setShowSpecialSectionInfo] = useState(false);
  const [tableStructures, setTableStructures] = useState([]);
  const specialInfoShownRef = useRef(false);
  
  const markDirty = () => { if (onDirtyChange) onDirtyChange(true); };
  const setFormDataDirty = (updater) => { setFormData(updater); markDirty(); };

  // Snapshot of formData when template was last loaded/saved — used for dirty comparison
  const initialFormDataRef = React.useRef(null);

  // Stable JSON stringify — sorts keys recursively so object key order doesn't matter
  const stableStringify = (val) => {
    if (val === null || typeof val !== 'object') return JSON.stringify(val);
    if (Array.isArray(val)) return '[' + val.map(stableStringify).join(',') + ']';
    const sorted = Object.keys(val).sort().map(k => JSON.stringify(k) + ':' + stableStringify(val[k]));
    return '{' + sorted.join(',') + '}';
  };

  // Fetch langs for LanguageSelector on mount
  useEffect(() => {
    const fetchLangs = async () => {
      setIsLoadingLangs(true);
      try {
        const resp = await axios.get(`${API_BASE_URL}/langs`, { headers });
        if (resp.data.status === 'success') {
          setLangsData(resp.data.langs || {});
        }
      } catch (e) {
        console.error('Error fetching langs:', e);
      } finally {
        setIsLoadingLangs(false);
      }
    };
    fetchLangs();
  }, []);

  // Auto dirty detection: compare current formData against initial snapshot
  useEffect(() => {
    if (!initialFormDataRef.current) return;
    const isDirty = stableStringify(formData) !== stableStringify(initialFormDataRef.current);
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [formData]);

  // submitFnRef gets populated after handleSubmit is defined (below)
  const submitFnRef = React.useRef(null);
  useImperativeHandle(ref, () => ({
    submitForm: () => submitFnRef.current?.(),
    getTemplateName: () => formData.template_name
  }));
  const [openParagraphDropdown, setOpenParagraphDropdown] = useState({});
  const dropdownRef = useRef(null);
  const [openSpecialSectionConfig, setOpenSpecialSectionConfig] = useState(null);
  const specialSectionConfigRef = useRef(null);

  const navigate = useNavigate();
 
   const API_BASE_URL = '/pceapi/genbi';
   const headers = {
     Authorization: GetCookie('access_token')
   };

   useEffect(() => {
     const fetchTableStructures = async () => {
       try {
         const resp = await axios.post(`${API_BASE_URL}/list-table-structures`, {}, { headers });
         if (resp.data.status === 'success' && resp.data.table_structures) {
           setTableStructures(resp.data.table_structures);
         }
       } catch (err) {
         console.error('Failed to fetch table structures:', err);
       }
     };
     fetchTableStructures();
   }, []);
 
   const compareOrderingIds = (a, b) => {
     const aStr = a || '0';
     const bStr = b || '0';
     const aIsSummary = aStr.startsWith('S');
     const bIsSummary = bStr.startsWith('S');
     // Summary sections (S-series) always come before regular sections
     if (aIsSummary && !bIsSummary) return -1;
     if (!aIsSummary && bIsSummary) return 1;
     // Both summary: compare numeric part after 'S'
     const aClean = aIsSummary ? aStr.slice(1) : aStr;
     const bClean = bIsSummary ? bStr.slice(1) : bStr;
     const aParts = aClean.split('.').map(Number);
     const bParts = bClean.split('.').map(Number);
     for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
       const diff = (aParts[i] || 0) - (bParts[i] || 0);
       if (diff !== 0) return diff;
     }
     return 0;
   };

   // sections array to dictionary structure
   const convertSectionsToDict = (sectionsArray) => {
     const sectionsDict = {};
     const sectionOrder = [];

     const sortedSections = [...sectionsArray].sort((a, b) =>
       compareOrderingIds(a.section_ordering_id, b.section_ordering_id)
     );

     sortedSections.forEach(section => {
       const key = section.id || section.temp_id || `temp_${Date.now()}_${Math.random()}`;

       const agentsDict = {};
       const agentOrder = [];

       if (section.agents) {
         const sortedAgents = [...section.agents].sort((a, b) =>
           (a.agent_ordering_id || 0) - (b.agent_ordering_id || 0)
         );

         sortedAgents.forEach(agent => {
           const agentKey = agent.id || agent.temp_id || `agent_temp_${Date.now()}_${Math.random()}`;
           agentsDict[agentKey] = { ...agent };
           if (!agent.isDeleted) {
             agentOrder.push(agentKey);
           }
         });
       }
 
       const oid = section.section_ordering_id || '';
       const isSPrefix = oid.startsWith('S') || oid.startsWith('s');

       sectionsDict[key] = {
         ...section,
         is_summary_section: section.is_summary_section || isSPrefix,
         agentsDict,
         agentOrder,
         vm_mappings: section.vm_mappings || []
       };
       

       if (!section.isDeleted) {
         sectionOrder.push(key);
       }
     });
 
     return { sectionsDict, sectionOrder };
   };
 
   // Convert dictionary structure back to array for API calls 
   const convertDictToSections = (sectionsDict, sectionOrder) => {
     return sectionOrder.map(sectionKey => {
       const section = sectionsDict[sectionKey];
       const agents = section.agentOrder.map(agentKey => {
         const agent = section.agentsDict[agentKey];
         // Return CLEAN agent object for CREATE API
         return {
           temp_id: agent.temp_id,
           agent_name: agent.agent_name || '',
           agent_context: agent.agent_context || '',
           additional_context: agent.additional_context || null,
           agent_ordering_id: agent.agent_ordering_id || 1,
           document_mappings: agent.document_mappings || [],
           reference_agent_ids: agent.reference_agent_ids || [],
           par_style: agent.par_style ?? null,
           table_structure_id: agent.table_structure_id ?? null,
           response: agent.response || null,
           parsed_response: agent.parsed_response || null,
           tag: agent.tag || null,
         };
       });
       
       // Return CLEAN section object for CREATE API
       return {
         temp_id: section.temp_id,
         section_name: section.section_name || '',
         parent_section_id: section.parent_section_id,
         section_ordering_id: section.section_ordering_id ?? '1',
         is_summary_section: section.is_summary_section || false,
         special_section_config: section.is_summary_section ? {
           is_outside_border: section.special_section_config?.is_outside_border || false,
           heading_bg_color: section.special_section_config?.heading_bg_color || null,
         } : null,
         autogen_after: section.autogen_after || null,
         vm_mappings: section.vm_mappings || [],
         agents: agents
       };
     });
   };
 
   // Convert to structured format for UPDATE API - SEND ALL SECTIONS
   const convertToStructuredSections = (sectionsDict, sectionOrder) => {
     const structured = {
       new: [],
       modified: [],
       deleted: []
     };
 
     // Process sections in order
     sectionOrder.forEach(sectionKey => {
       const section = sectionsDict[sectionKey];
       
       // Prepare clean agents array
       const agents = section.agentOrder.map(agentKey => {
         const agent = section.agentsDict[agentKey];
         const isDirty = !agent.id || agent.isModified === true; // New agents are always dirty
         return {
           id: agent.id,
           temp_id: agent.temp_id,
           agent_name: agent.agent_name || '',
           agent_context: agent.agent_context || '',
           additional_context: agent.additional_context || null,
           agent_ordering_id: agent.agent_ordering_id || 1,
           document_mappings: agent.document_mappings || [],
           reference_agent_ids: agent.reference_agent_ids || [],
           par_style: agent.par_style ?? null,
           table_structure_id: agent.table_structure_id ?? null,
           is_dirty: isDirty,
           explicit_delete: false,
           response: agent.response || null,
           tag: agent.tag || null,
         };
       });

       // Also include agents marked as isDeleted with explicit_delete flag
       Object.keys(section.agentsDict).forEach(agentKey => {
         const agent = section.agentsDict[agentKey];
         if (agent.isDeleted && agent.id) {
           agents.push({
             id: agent.id,
             agent_name: agent.agent_name || '',
             agent_context: agent.agent_context || '',
             additional_context: agent.additional_context || null,
             agent_ordering_id: agent.agent_ordering_id || 1,
             document_mappings: agent.document_mappings || [],
             reference_agent_ids: agent.reference_agent_ids || [],
             is_dirty: false,
             explicit_delete: true
           });
         }
       });
 
       const sectionData = {
         id: section.id,
         temp_id: section.temp_id,
         section_name: section.section_name || '',
         parent_section_id: section.parent_section_id,
         section_ordering_id: section.section_ordering_id ?? '1',
         is_summary_section: section.is_summary_section || false,
         special_section_config: section.is_summary_section ? {
           is_outside_border: section.special_section_config?.is_outside_border || false,
           heading_bg_color: section.special_section_config?.heading_bg_color || null,
         } : null,
         autogen_after: section.autogen_after || null,
         vm_mappings: section.vm_mappings || [],
         agents: agents
       };
 
       if (section.id) {
         // Existing section - always include in modified for safety
         // Backend will determine if actual UPDATE is needed
         structured.modified.push(sectionData);
       } else {
         // New section
         structured.new.push(sectionData);
       }
     });
 
     // Add deleted sections
     Object.values(sectionsDict).forEach(section => {
       if (section.isDeleted && section.id) {
         structured.deleted.push({
           id: section.id,
           
         });
       }
     });
 
     return structured;
   };
   // Generate hierarchical ordering ID
   const generateOrderingId = (parentKey, sectionsDict, sectionOrder) => {
     if (!parentKey) {
       // Root level - find max root number
       const rootSections = sectionOrder.filter(key => {
         const section = sectionsDict[key];
         return section && !section.parent_section_id;
       });
       const maxNumber = rootSections.reduce((max, key) => {
         const section = sectionsDict[key];
         const orderingId = section?.section_ordering_id || '0';
         const num = parseInt(orderingId.split('.')[0]);
         return Math.max(max, isNaN(num) ? 0 : num);
       }, 0);
        const fmt = formData.format_json?.numberingFormat;
const startVal = (fmt === "none" || fmt === undefined) ? (formData.format_json?.numberingStartValue ?? 1) : 1;
if (rootSections.length === 0) return String(startVal ?? 1);
return String(Math.max(maxNumber + 1, startVal ?? 1));
     } else {
       // Child level
       const parentSection = sectionsDict[parentKey];
       if (!parentSection) return '1';
       
       const parentOrderingId = parentSection.section_ordering_id;
       const siblings = sectionOrder.filter(key => {
         const section = sectionsDict[key];
         return section && section.parent_section_id === parentKey;
       });
       
       const maxChildNumber = siblings.reduce((max, key) => {
         const section = sectionsDict[key];
         const orderingId = section?.section_ordering_id || `${parentOrderingId}.0`;
         const parts = orderingId.split('.');
         const childNum = parseInt(parts[parts.length - 1]);
         return Math.max(max, isNaN(childNum) ? 0 : childNum);
       }, 0);
       
       return `${parentOrderingId}.${maxChildNumber + 1}`;
     }
   };
 
   // Find all descendant sections recursively
   const findAllDescendants = (parentKey, sectionsDict, sectionOrder) => {
     const descendants = [];
     
     if (!sectionsDict[parentKey]) return descendants;
     
     // Find direct children
     const directChildren = sectionOrder.filter(key => {
       const section = sectionsDict[key];
       return section && section.parent_section_id === parentKey;
     });
     
     directChildren.forEach(childKey => {
       descendants.push(childKey);
       // Recursively find grandchildren
       const grandChildren = findAllDescendants(childKey, sectionsDict, sectionOrder);
       descendants.push(...grandChildren);
     });
     
     return descendants;
   };
 
   // Recalculate ordering IDs only for affected siblings
   const recalculateSiblingOrderingIds = (parentKey, sectionsDict, sectionOrder, overrideFormatJson = null) => {
     // Safety checks
     if (!sectionsDict || typeof sectionsDict !== 'object' || !Array.isArray(sectionOrder)) {
       return sectionsDict;
     }
 
     // If parentKey is provided, ensure the parent section exists
     if (parentKey && !sectionsDict[parentKey]) {
       console.warn(`Parent section ${parentKey} not found during recalculation`);
       return sectionsDict;
     }
 
     const siblings = sectionOrder.filter(key => {
       const section = sectionsDict[key];
       if (!section || section.isDeleted) return false;
       
       return (parentKey ? section.parent_section_id === parentKey : !section.parent_section_id);
     });
 
     const updatedSectionsDict = { ...sectionsDict };

     // Skip summary sections (S-series) — never renumber them
     const regularSiblings = siblings.filter(key => {
       const sec = updatedSectionsDict[key];
       return !sec.is_summary_section && !(sec.section_ordering_id || '').startsWith('S');
     });

     regularSiblings.forEach((siblingKey, index) => {
       const section = updatedSectionsDict[siblingKey];

       // Safety check: ensure section exists
       if (!section) return;
       
       const newOrderingId = parentKey 
         ? `${updatedSectionsDict[parentKey]?.section_ordering_id || '0'}.${index + 1}`
         : (() => {
    const activeFormat = overrideFormatJson || formData.format_json;
    const fmt = activeFormat?.numberingFormat;
    const startVal = (fmt === "none" || fmt === undefined) ? (activeFormat?.numberingStartValue ?? 1) : 1;
    return String(startVal + index);
  })();
       
       if (section.section_ordering_id !== newOrderingId) {
         updatedSectionsDict[siblingKey] = {
           ...section,
           section_ordering_id: newOrderingId,
           isModified: section.id ? true : undefined
         };
 
         // Recursively update children - with safety checks
         const children = sectionOrder.filter(key => {
           const childSection = sectionsDict[key];
           return childSection && !childSection.isDeleted && childSection.parent_section_id === siblingKey;
         });
         
         if (children.length > 0) {
           try {
             const childUpdates = recalculateSiblingOrderingIds(siblingKey, updatedSectionsDict, sectionOrder, overrideFormatJson);
             if (childUpdates && typeof childUpdates === 'object') {
               Object.assign(updatedSectionsDict, childUpdates);
             }
           } catch (error) {
             console.error('Error updating children ordering:', error);
             // Continue without updating children if there's an error
           }
         }
       }
     });
 
     // SAFETY NET: forcibly restore every S-series section's ordering from original input
     Object.keys(sectionsDict).forEach(key => {
       const orig = sectionsDict[key];
       if (orig && (orig.is_summary_section || (orig.section_ordering_id || '').startsWith('S'))) {
         updatedSectionsDict[key] = { ...updatedSectionsDict[key], section_ordering_id: orig.section_ordering_id, is_summary_section: orig.is_summary_section };
       }
     });

     return updatedSectionsDict;
   };

   const recalculateSummaryOrderingIds = (sectionsDict, sectionOrder) => {
     const updatedSectionsDict = { ...sectionsDict };

     const isRootSummary = (sec) => {
       if (!sec || !isSummarySection(sec) || sec.isDeleted) return false;
       if (sec.parent_section_id) return false;
       const oid = sec.section_ordering_id || '';
       return !oid.includes('.');
     };

     const isChildOfSummary = (sec, parentKey) => {
       if (!sec || !isSummarySection(sec) || sec.isDeleted) return false;
       return sec.parent_section_id === parentKey;
     };

     const rootSummarySections = sectionOrder.filter(key => isRootSummary(updatedSectionsDict[key]));

     rootSummarySections.forEach((key, index) => {
       const newOrderingId = `S${index + 1}`;
       const section = updatedSectionsDict[key];
       if (section.section_ordering_id !== newOrderingId) {
         updatedSectionsDict[key] = {
           ...section,
           section_ordering_id: newOrderingId,
           isModified: section.id ? true : undefined
         };
       }

       const children = sectionOrder.filter(childKey => isChildOfSummary(updatedSectionsDict[childKey], key));

       children.forEach((childKey, childIndex) => {
         const childOrderingId = `${newOrderingId}.${childIndex + 1}`;
         const child = updatedSectionsDict[childKey];
         updatedSectionsDict[childKey] = {
           ...child,
           section_ordering_id: childOrderingId,
           isModified: child.id ? true : undefined
         };
       });
     });

     return updatedSectionsDict;
   };

   // Find correct insertion position for nested sections
   const findInsertionPosition = (parentKey, sectionOrder, sectionsDict) => {
     if (!parentKey) {
       return sectionOrder.length; // Add to end for root level
     }
 
     const parentIndex = sectionOrder.indexOf(parentKey);
     if (parentIndex === -1) return sectionOrder.length;
 
     // Find the last descendant of the parent
     let insertIndex = parentIndex + 1;
     const parentSection = sectionsDict[parentKey];
     if (!parentSection) return sectionOrder.length;
     
     const parentOrderingId = parentSection.section_ordering_id;
     
     for (let i = parentIndex + 1; i < sectionOrder.length; i++) {
       const sectionKey = sectionOrder[i];
       const section = sectionsDict[sectionKey];
       if (!section) continue;
       
       const orderingId = section.section_ordering_id;
       
       if (orderingId && orderingId.startsWith(parentOrderingId + '.')) {
         insertIndex = i + 1;
       } else {
         break;
       }
     }
     
     return insertIndex;
   };

   // ✅ Helper function to get all fabric documents
   const getAllFabricDocuments = (tag = null) => {
     const allDocIds = [];
     fabricTreeData.forEach(fabric => {
       fabric.threads?.forEach(thread => {
         thread.documents?.forEach(doc => {
           if (tag) {
             // Tag-based filtering: only include docs whose classification matches the agent's tag
             const docClassification = (doc.classification || '').trim().toLowerCase();
             const agentTag = tag.trim().toLowerCase();
             if (docClassification === agentTag) {
               allDocIds.push(doc.id);
             }
           } else {
             allDocIds.push(doc.id);
           }
         });
       });
     });
     return allDocIds.map(docId => ({
       doc_id: docId,
       selected: true
     }));
   };

   // Load template data for editing
   useEffect(() => {
  if (editingTemplate) {
    setIsInitialFabricLoad(true);
    setIsDocGeneratedImport(false); // edit mode — never show response toggle
    loadTemplateDetails();
  } else {
    // Reset form for create mode
    setFormData({
      template_name: '',
      fabric_id: null,
      fabric_name: '',
      sectionsDict: {},
      sectionOrder: [],
      vm_config: false,
      lang_id: 1,
      format_json: DEFAULT_REPORT_CONFIG,
    });
    initialFormDataRef.current = null;
    setUseValueMessage(false);
    setIsInitialFabricLoad(false);
    setIsDocGeneratedImport(false);
    setImportedLibraryTemplateId(null);
    setImportedLibraryTemplateName('');
    setHeadingMappingId(null);
    setSavedMapping(null);
    setShowResponsePanel({});
    // Reset language display to English default
    setSelectedLanguage({ id: 1, language_name: 'English' });

    endProcessing();
  }
}, [editingTemplate]);
 
   // Load fabric documents when fabric changes
   useEffect(() => {
     if (formData.fabric_id && fabricTreeData.length > 0) {
       // Skip update ONLY for update mode + initial load
       // All other cases should update:
       // - Create mode + initial selection
       // - Create mode + fabric change  
       // - Update mode + fabric change
       if (!(editingTemplate && isInitialFabricLoad)) {
         updateAllAgentsWithFabricDocuments();
       }
       
       //After initial load is processed, reset flag
       if (isInitialFabricLoad) {
         setIsInitialFabricLoad(false);
       }
     }
   }, [fabricTreeData]);
 useEffect(() => {
  if (preloadedTemplate && !editingTemplate) {
    const { sectionsDict, sectionOrder } = convertSectionsToDict(
      preloadedTemplate.sections || []
    );
    const preloadLangId = preloadedTemplate.lang_id || 1;
    setFormData({
      template_name: preloadedTemplate.template_name,
      fabric_id: preloadedTemplate.fabric_id,
      fabric_name: preloadedTemplate.fabric_name,
      sectionsDict,
      sectionOrder,
      vm_config: false,
      lang_id: preloadLangId,
      format_json: preloadedTemplate.format_json || DEFAULT_REPORT_CONFIG,
      global_prompt: preloadedTemplate.global_prompt || ''
    });
    // Reset to English for preloaded; AgentTranslationModal will handle actual selection
    setSelectedLanguage({ id: preloadLangId, language_name: 'English' });
    if (preloadedTemplate.fabric_id) {
      loadFabricDocuments(preloadedTemplate.fabric_id);
    }
    endProcessing();
  }
}, [preloadedTemplate]);

   const loadTemplateDetails = async () => {
     if (!editingTemplate) return;
     setIsLoading(true);
     try {
       const response = await axios.post(
         `${API_BASE_URL}/get-template-details`,
         {
           template_id: editingTemplate.id,
           fabric_id: editingTemplate.fabric_id
         },
         { headers }
       );
 
       if (response.data.status === 'success') {
         const templateData = response.data.data;
         let { sectionsDict, sectionOrder } = convertSectionsToDict(templateData.sections || []);
         sectionsDict = recalculateSummaryOrderingIds(sectionsDict, sectionOrder);
         const cur_lang_id = templateData.lang_id ?? 1;

         setFormData({
           template_name: templateData.template_name,
           fabric_id: templateData.fabric_id,
           fabric_name: templateData.fabric_name,
           sectionsDict,
           sectionOrder,
           vm_config: templateData.vm_config,
           lang_id: cur_lang_id,
           format_json: templateData.format_json || DEFAULT_REPORT_CONFIG,
           global_prompt: templateData.global_prompt || ''
         });
         initialFormDataRef.current = {
           template_name: templateData.template_name,
           fabric_id: templateData.fabric_id,
           fabric_name: templateData.fabric_name,
           sectionsDict,
           sectionOrder,
           vm_config: templateData.vm_config,
           lang_id: cur_lang_id,
           format_json: templateData.format_json || DEFAULT_REPORT_CONFIG
         };

         // Resolve language name for display using langs API
         try {
           const langsResp = await axios.get(`${API_BASE_URL}/langs`, { headers });
           if (langsResp.data.status === 'success') {
             const langsObj = langsResp.data.langs || {};
             const langName = langsObj[cur_lang_id] || langsObj[String(cur_lang_id)] || 'English';
             setSelectedLanguage({ id: cur_lang_id, language_name: langName });
           }
         } catch {
           setSelectedLanguage({ id: cur_lang_id, language_name: 'English' });
         }

         setHeadingMappingId(templateData.heading_mapping_id || null);
         setUseValueMessage(templateData.vm_config);
         setIsLoading(false);
         endProcessing();
         if (templateData.vm_config && templateData.vm_config_data) {
           setVmConfiguration(templateData.vm_config_data);
         }
         if (templateData.fabric_id) {
           await loadFabricDocuments(templateData.fabric_id);
         }
       } else {
         endProcessing();
         onError(response.data.message || 'Failed to load template details');
       }
     } catch (error) {
      setIsLoading(false);
       endProcessing();
       onError('Failed to load template details');
     }
   };
 
   // Always preserve existing sections during fabric changes
   const handleFabricSelection = async (fabricId, fabricName) => {
     if (fabricId === formData.fabric_id) {
       return;
     }

     if (isDocGeneratedImport && formData.sectionOrder.length > 0) {
       setPendingFabricSwitch({ fabricId, fabricName });
       setShowFabricSwitchConfirm(true);
       return;
     }

     setFormData(prev => ({
       ...prev,
       fabric_id: fabricId,
       fabric_name: fabricName,
       sectionsDict: prev.sectionsDict,  // Always preserve sections
       sectionOrder: prev.sectionOrder   // Always preserve order
     }));
 
     if (fabricId) {
       //  User changed fabric, not initial load
       setIsInitialFabricLoad(false);
       await loadFabricDocuments(fabricId);
     }
   };

   const handleConfirmFabricSwitch = async () => {
     if (!pendingFabricSwitch) return;
     const { fabricId, fabricName } = pendingFabricSwitch;

     setFormData({
       template_name: '',
       fabric_id: fabricId,
       fabric_name: fabricName,
       sectionsDict: {},
       sectionOrder: [],
       vm_config: false,
       lang_id: 1,
       format_json: DEFAULT_REPORT_CONFIG,
       global_prompt: '',
       frontpage: null
     });
     setIsDocGeneratedImport(false);
     setImportedLibraryTemplateId(null);
     setImportedLibraryTemplateName('');
     setShowResponsePanel({});
     setMagicPrompt('');
     setSelectedLanguage({ id: 1, language_name: 'English' });

     setShowFabricSwitchConfirm(false);
     setPendingFabricSwitch(null);

     setIsInitialFabricLoad(false);
     if (fabricId) {
       await loadFabricDocuments(fabricId);
     }
   };

   const handleCancelFabricSwitch = () => {
     setShowFabricSwitchConfirm(false);
     setPendingFabricSwitch(null);
   };
 // Called by AgentTranslationModal: langId is number, langName resolved from modal's internal state
 const handleTranslateSelect = (langId, langName) => {
   setSelectedLanguage({ id: langId, language_name: langName || 'Language ' + langId });
   setFormData(prev => ({ ...prev, lang_id: langId }));
   setIsLangModalOpen(false);
 };
   const loadFabricDocuments = async (fabricId) => {
     try {
       const response = await axios.post(
         `${API_BASE_URL}/fabric_unstructured_data_tree`,
         {
           fabric_id: fabricId,        
           include_documents: true,
           page: 1,                   
           size: 25                  
         },
         { headers }
       );
 
       if (response.data.status === 'success') {
         setFabricTreeData(response.data.data);
       }
     } catch (error) {
       console.error('Error loading project documents:', error);
     }
   };
 
   const updateAllAgentsWithFabricDocuments = () => {
     if (!fabricTreeData.length || !formData.fabric_id) return;
 
     // Pre-compute all-docs mapping for agents without tags
     const allDocumentMappings = getAllFabricDocuments();
     
     setFormData(prev => {
       // Create a completely new sectionsDict to avoid nested spreading
       const updatedSectionsDict = {};
       
       // Process all sections in a single loop
       Object.keys(prev.sectionsDict).forEach(sectionKey => {
         const section = prev.sectionsDict[sectionKey];
         
         // Create new agentsDict for this section
         const updatedAgentsDict = {};
         
         // Process all agents in this section
         Object.keys(section.agentsDict).forEach(agentKey => {
           const agent = section.agentsDict[agentKey];
           
           // Tag-based mapping: if agent has tag, only map matching docs
           const agentMappings = agent.tag
             ? getAllFabricDocuments(agent.tag)
             : allDocumentMappings;
           
           // Create new agent object with updated document mappings
           updatedAgentsDict[agentKey] = {
             ...agent,
             document_mappings: agentMappings
           };
         });
         
         // Create new section object
         updatedSectionsDict[sectionKey] = {
           ...section,
           agentsDict: updatedAgentsDict
         };
       });
       return {
         ...prev,
         sectionsDict: updatedSectionsDict
       };
     });
   };
 
   // Add new section
   const addSection = (parentKey = null) => {
     const newKey = `temp_${Date.now()}_${Math.random()}`;
     const newOrderingId = generateOrderingId(parentKey, formData.sectionsDict, formData.sectionOrder);
     const insertPosition = findInsertionPosition(parentKey, formData.sectionOrder, formData.sectionsDict);
     
     const newSection = {
       temp_id: newKey,
       section_name: '',
       parent_section_id: parentKey,
       section_ordering_id: newOrderingId,
       agentsDict: {},
       agentOrder: [],
       isNew: true
     };
 
     setFormData(prev => {
       const newSectionOrder = [...prev.sectionOrder];
       newSectionOrder.splice(insertPosition, 0, newKey);
       
       return {
         ...prev,
         sectionsDict: {
           ...prev.sectionsDict,
           [newKey]: newSection
         },
         sectionOrder: newSectionOrder
       };
     });
   };
 
   // Add new agent to section
   const addAgent = (sectionKey) => {
     const section = formData.sectionsDict[sectionKey];
     if (!section) return;
     
     const newAgentKey = `agent_temp_${Date.now()}_${Math.random()}`;
     const maxOrderingId = section.agentOrder.length > 0 
       ? Math.max(...section.agentOrder.map(key => 
           section.agentsDict[key]?.agent_ordering_id || 0
         ))
       : 0;
 
     // ✅ Always use all fabric documents
     const defaultDocumentMappings = getAllFabricDocuments();
 
     const newAgent = {
       temp_id: newAgentKey,
       agent_name: '',
       agent_context: '',
       additional_context: null,
       agent_ordering_id: maxOrderingId + 1,
       document_mappings: defaultDocumentMappings,
       reference_agent_ids: [],
       par_style: null,
       table_structure_id: null,
       isNew: true
     };
 
     setFormData(prev => ({
       ...prev,
       sectionsDict: {
         ...prev.sectionsDict,
         [sectionKey]: {
           ...prev.sectionsDict[sectionKey],
           agentsDict: {
             ...prev.sectionsDict[sectionKey].agentsDict,
             [newAgentKey]: newAgent
           },
           agentOrder: [...prev.sectionsDict[sectionKey].agentOrder, newAgentKey],
           hasModifiedAgents: true  // Mark section as having modified agents
         }
       }
     }));
   };
 
   // Delete section
   const deleteSection = (sectionKey) => {
     setFormData(prev => {
       const section = prev.sectionsDict[sectionKey];
       if (!section) return prev;
       
       let updatedSectionsDict = { ...prev.sectionsDict };
       let updatedSectionOrder = [...prev.sectionOrder];
 
       // Find all descendants that need to be deleted
       const allDescendants = findAllDescendants(sectionKey, updatedSectionsDict, updatedSectionOrder);
       
       // Delete/mark descendants and parent
       const sectionsToRemove = [sectionKey, ...allDescendants];
       sectionsToRemove.forEach(keyToRemove => {
         const sectionToDelete = updatedSectionsDict[keyToRemove];
         if (sectionToDelete) {
           if (sectionToDelete.id) {
             // Existing section - mark as deleted
             updatedSectionsDict[keyToRemove] = {
               ...sectionToDelete,
               isDeleted: true
             };
           } else {
             // New section - remove completely
             delete updatedSectionsDict[keyToRemove];
           }
         }
       });
       
       // Remove from order
       updatedSectionOrder = updatedSectionOrder.filter(key => 
         !sectionsToRemove.includes(key)
       );
 
       // Recalculate ordering for siblings at the same level as the deleted parent
       const parentKey = section.parent_section_id;
       try {
         updatedSectionsDict = recalculateSiblingOrderingIds(parentKey, updatedSectionsDict, updatedSectionOrder);
       } catch (error) {
         console.error('Error recalculating sibling ordering:', error);
       }

       if (isSummarySection(section)) {
         try {
           updatedSectionsDict = recalculateSummaryOrderingIds(updatedSectionsDict, updatedSectionOrder);
         } catch (error) {
           console.error('Error recalculating summary ordering:', error);
         }
       }

       return {
         ...prev,
         sectionsDict: updatedSectionsDict,
         sectionOrder: updatedSectionOrder
       };
     });
   };

   // Delete agent
   const deleteAgent = (sectionKey, agentKey) => {
     setFormData(prev => {
       const section = prev.sectionsDict[sectionKey];
       if (!section) return prev;
       
       const agent = section.agentsDict[agentKey];
       if (!agent) return prev;
       
       let updatedAgentsDict = { ...section.agentsDict };
       let updatedAgentOrder = [...section.agentOrder];
 
       if (agent.id) {
         // Existing agent - mark as deleted
         updatedAgentsDict[agentKey] = {
           ...agent,
           isDeleted: true
         };
       } else {
         // New agent - remove completely
         delete updatedAgentsDict[agentKey];
       }
 
       // Remove from order
       updatedAgentOrder = updatedAgentOrder.filter(key => key !== agentKey);
 
       // Recalculate agent ordering IDs only within this section
       updatedAgentOrder.forEach((agentKey, index) => {
         const agent = updatedAgentsDict[agentKey];
         if (agent && agent.agent_ordering_id !== index + 1) {
           updatedAgentsDict[agentKey] = {
             ...agent,
             agent_ordering_id: index + 1,
             isModified: agent.id ? true : undefined
           };
         }
       });
 
       return {
         ...prev,
         sectionsDict: {
           ...prev.sectionsDict,
           [sectionKey]: {
             ...section,
             agentsDict: updatedAgentsDict,
             agentOrder: updatedAgentOrder,
             hasModifiedAgents: true  // Mark section as having modified agents
           }
         }
       };
     });
   };
 
   // Update section field
   const updateSectionField = (sectionKey, field, value) => {
     setFormData(prev => ({
       ...prev,
       sectionsDict: {
         ...prev.sectionsDict,
         [sectionKey]: {
           ...prev.sectionsDict[sectionKey],
           [field]: value,
           isModified: prev.sectionsDict[sectionKey].id ? true : undefined
         }
       }
     }));
   };

   const updateSpecialSectionConfig = (sectionKey, configKey, value) => {
     setFormData(prev => {
       const section = prev.sectionsDict[sectionKey];
       const currentConfig = section.special_section_config || {};
       return {
         ...prev,
         sectionsDict: {
           ...prev.sectionsDict,
           [sectionKey]: {
             ...section,
             special_section_config: { ...currentConfig, [configKey]: value },
             isModified: section.id ? true : undefined
           }
         }
       };
     });
   };

   useEffect(() => {
     const handleSpecialConfigClickOutside = (event) => {
       if (specialSectionConfigRef.current && !specialSectionConfigRef.current.contains(event.target)) {
         setOpenSpecialSectionConfig(null);
       }
     };
     const handleSpecialConfigScroll = () => setOpenSpecialSectionConfig(null);
     document.addEventListener("mousedown", handleSpecialConfigClickOutside);
     document.addEventListener("scroll", handleSpecialConfigScroll, true);
     return () => {
       document.removeEventListener("mousedown", handleSpecialConfigClickOutside);
       document.removeEventListener("scroll", handleSpecialConfigScroll, true);
     };
   }, []);
 
   // Update agent field
   const updateAgentField = (sectionKey, agentKey, field, value) => {
     setFormData(prev => {
       const section = prev.sectionsDict[sectionKey];
       const agent = section.agentsDict[agentKey];
       
       return {
         ...prev,
         sectionsDict: {
           ...prev.sectionsDict,
           [sectionKey]: {
             ...section,
             agentsDict: {
               ...section.agentsDict,
               [agentKey]: {
                 ...agent,
                 [field]: value,
                 isModified: agent.id ? true : undefined
               }
             },
             hasModifiedAgents: true  // Mark section as having modified agents
           }
         }
       };
     });
   };
 
   // Open document selector for agent
   const openDocumentSelector = (sectionKey, agentKey) => {
     setCurrentAgentIndex({ sectionKey, agentKey });
     setDocumentSelectorOpen(true);
   };

   const openConfigureModal = (sectionKey, agentKey) => {
    setAgentToConfigure({ sectionKey, agentKey });
    setAgentConfigureOpen(true);
  };

  const closeAgentConfigure = () => {
    setAgentConfigureOpen(false);
  };

  const openReportFormatting = () => {
  setShowReportFormatting(true);
};

  const handleReportFormattingSave = (formattingData) => {
    setFormData(prev => {
        const fmt = formattingData?.numberingFormat;

        // Snapshot all S-series sections before any recalculation
        const sSectionSnapshots = {};
        Object.keys(prev.sectionsDict).forEach(key => {
          const sec = prev.sectionsDict[key];
          if (sec && (sec.is_summary_section || (sec.section_ordering_id || '').startsWith('S'))) {
            sSectionSnapshots[key] = { section_ordering_id: sec.section_ordering_id, is_summary_section: sec.is_summary_section };
          }
        });

        let newSectionsDict = prev.sectionsDict;
        if (fmt === "none") {
          newSectionsDict = recalculateSiblingOrderingIds(null, prev.sectionsDict, prev.sectionOrder, formattingData);
        }

        // Forcibly restore S-series — no matter what happened above
        Object.keys(sSectionSnapshots).forEach(key => {
          if (newSectionsDict[key]) {
            newSectionsDict = { ...newSectionsDict, [key]: { ...newSectionsDict[key], ...sSectionSnapshots[key] } };
          }
        });

        return { ...prev, format_json: formattingData, sectionsDict: newSectionsDict };
    });
    closeReportFormatting();
  };
  const closeReportFormatting = () => {
    setShowReportFormatting(false);
  };
 
   // Handle document selection for agent
   const handleDocumentSelection = (selectedDocIds) => {
     const { sectionKey, agentKey } = currentAgentIndex;
     const documentMappings = selectedDocIds.map(docId => ({
       doc_id: docId,
       selected: true
     }));
 
     updateAgentField(sectionKey, agentKey, 'document_mappings', documentMappings);
     setDocumentSelectorOpen(false);
   };
 
   // Helper: check if a section is S-series (summary) — used to block reordering
   const isSummarySection = (section) => {
     if (!section) return false;
     if (section.is_summary_section) return true;
     const oid = (section.section_ordering_id || '');
     return oid.startsWith('S') || oid.startsWith('s');
   };

   // Move section up/down within the same parent level
   const moveSectionOrder = (sectionKey, direction) => {
     setFormData(prev => {
       const section = prev.sectionsDict[sectionKey];
       if (!section) return prev;

       const sectionIsSummary = isSummarySection(section);
       const parentKey = section.parent_section_id;

       // Get siblings at the same level — only same type (S-series with S-series, ordinary with ordinary)
       const siblings = prev.sectionOrder.filter(key => {
         const siblingSection = prev.sectionsDict[key];
         if (!siblingSection) return false;
         const siblingIsSummary = isSummarySection(siblingSection);
         // Must be same type (both summary or both ordinary)
         if (siblingIsSummary !== sectionIsSummary) return false;
         return parentKey ? siblingSection.parent_section_id === parentKey : !siblingSection.parent_section_id;
       });

       const currentSiblingIndex = siblings.indexOf(sectionKey);
       const targetSiblingIndex = direction === 'up' ? currentSiblingIndex - 1 : currentSiblingIndex + 1;

       // Check if move is valid
       if (targetSiblingIndex < 0 || targetSiblingIndex >= siblings.length) {
         return prev;
       }

       const targetSectionKey = siblings[targetSiblingIndex];

       // Find all descendants of both sections (current and target)
       const currentDescendants = findAllDescendants(sectionKey, prev.sectionsDict, prev.sectionOrder);
       const targetDescendants = findAllDescendants(targetSectionKey, prev.sectionsDict, prev.sectionOrder);

       // Create new section order
       let newSectionOrder = [...prev.sectionOrder];

       // Remove current section and all its descendants
       const currentGroup = [sectionKey, ...currentDescendants];
       newSectionOrder = newSectionOrder.filter(key => !currentGroup.includes(key));

       // Find where to insert the moved section group
       const targetGroup = [targetSectionKey, ...targetDescendants];
       let insertIndex;

       if (direction === 'up') {
         // Insert before the target section
         insertIndex = newSectionOrder.indexOf(targetSectionKey);
       } else {
         // Insert after the target section and all its descendants
         const lastTargetIndex = targetGroup.reduce((maxIndex, key) => {
           const keyIndex = newSectionOrder.indexOf(key);
           return keyIndex > maxIndex ? keyIndex : maxIndex;
         }, -1);
         insertIndex = lastTargetIndex + 1;
       }

       // Insert the current section group at the new position
       newSectionOrder.splice(insertIndex, 0, ...currentGroup);

       // Recalculate ordering IDs
       let updatedSectionsDict = { ...prev.sectionsDict };
       try {
         if (sectionIsSummary) {
           // Recalculate S-series numbering
           updatedSectionsDict = recalculateSummaryOrderingIds(updatedSectionsDict, newSectionOrder);
           // Deep-renumber children for each S-root
           const sRoots = newSectionOrder.filter(key => {
             const sec = updatedSectionsDict[key];
             return sec && isSummarySection(sec) && !sec.parent_section_id;
           });
           sRoots.forEach(rootKey => {
             assignSPrefixRecursive(rootKey, updatedSectionsDict[rootKey].section_ordering_id, updatedSectionsDict, newSectionOrder);
           });
         } else {
           updatedSectionsDict = recalculateSiblingOrderingIds(parentKey, updatedSectionsDict, newSectionOrder);
         }
       } catch (error) {
         console.error('Error recalculating section ordering:', error);
         return prev;
       }

       return {
         ...prev,
         sectionsDict: updatedSectionsDict,
         sectionOrder: newSectionOrder
       };
     });
   };

   // Helper function to check if section can be moved up/down
   const canMoveSectionUp = (sectionKey) => {
     const section = formData.sectionsDict[sectionKey];
     if (!section) return false;

     const sectionIsSummary = isSummarySection(section);
     const parentKey = section.parent_section_id;
     const siblings = formData.sectionOrder.filter(key => {
       const siblingSection = formData.sectionsDict[key];
       if (!siblingSection) return false;
       if (isSummarySection(siblingSection) !== sectionIsSummary) return false;
       return parentKey ? siblingSection.parent_section_id === parentKey : !siblingSection.parent_section_id;
     });

     return siblings.indexOf(sectionKey) > 0;
   };

   const canMoveSectionDown = (sectionKey) => {
     const section = formData.sectionsDict[sectionKey];
     if (!section) return false;

     const sectionIsSummary = isSummarySection(section);
     const parentKey = section.parent_section_id;
     const siblings = formData.sectionOrder.filter(key => {
       const siblingSection = formData.sectionsDict[key];
       if (!siblingSection) return false;
       if (isSummarySection(siblingSection) !== sectionIsSummary) return false;
       return parentKey ? siblingSection.parent_section_id === parentKey : !siblingSection.parent_section_id;
     });

     return siblings.indexOf(sectionKey) < siblings.length - 1;
   };

  const beginProcessing = (message) => {
    setBlockingOverlayCount(count => count + 1);
    if (onProcessingStart) onProcessingStart(message);
  };

  const endProcessing = () => {
    setBlockingOverlayCount(count => Math.max(0, count - 1));
    if (onProcessingEnd) onProcessingEnd();
  };

  const handleImportFullScreenChange = (isFullScreen) => {
    setBlockingOverlayCount(count =>
      isFullScreen ? count + 1 : Math.max(0, count - 1)
    );
  };

  const isSectionTreeCompact =
    blockingOverlayCount > 0 || !!savedMapping || showReportFormatting;

   const moveAgentOrder = (sectionKey, agentKey, direction) => {
     setFormData(prev => {
       const section = prev.sectionsDict[sectionKey];
       if (!section) return prev;
       
       const currentIndex = section.agentOrder.indexOf(agentKey);
       const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
       
       if (targetIndex >= 0 && targetIndex < section.agentOrder.length) {
         const newAgentOrder = [...section.agentOrder];
         [newAgentOrder[currentIndex], newAgentOrder[targetIndex]] = 
         [newAgentOrder[targetIndex], newAgentOrder[currentIndex]];
         
         // Update ordering IDs
         const updatedAgentsDict = { ...section.agentsDict };
         newAgentOrder.forEach((agentKey, index) => {
           const agent = updatedAgentsDict[agentKey];
           if (agent) {
             updatedAgentsDict[agentKey] = {
               ...agent,
               agent_ordering_id: index + 1,
               isModified: agent.id ? true : undefined
             };
           }
         });
 
         return {
           ...prev,
           sectionsDict: {
             ...prev.sectionsDict,
             [sectionKey]: {
               ...section,
               agentsDict: updatedAgentsDict,
               agentOrder: newAgentOrder,
               hasModifiedAgents: true  // Mark section as having modified agents
             }
           }
         };
       }
       
       return prev;
     });
   };
 
   // Handle form submission
   const handleSubmit = async (e) => {
     e.preventDefault();
 
     if (!formData.template_name.trim()) {
       onError('Template name is required');
       return;
     }
 
     if (!formData.fabric_id) {
       onError('Please select a project');
       return;
     }
 
 
     if (formData.sectionOrder.length === 0) {
       onError('At least one section is required');
       return;
     }
 
     setIsLoading(true);
     
     const processingMessage = editingTemplate ? 'Updating template' : 'Creating template';
     beginProcessing(processingMessage);
 
     try {
       const endpoint = editingTemplate ? '/update-template' : '/create-template';
       
       let requestData;
       
       if (editingTemplate) {
         // UPDATE: Use structured format
         const structuredSections = convertToStructuredSections(formData.sectionsDict, formData.sectionOrder);
         
         requestData = {
           template_id: editingTemplate.id,
           template_name: formData.template_name,
           fabric_id: formData.fabric_id,
           vm_config: formData.vm_config || false,
           sections: structuredSections,
           lang_id: formData.lang_id || 1,
           format_json: formData.format_json || null,
           global_prompt: formData.global_prompt || null,
           frontpage: formData.frontpage || null
         };
       } else {
         // CREATE: Use array format
         const sectionsArray = convertDictToSections(formData.sectionsDict, formData.sectionOrder);
         
         requestData = {
           template_name: formData.template_name,
           fabric_id: formData.fabric_id,
           vm_config: formData.vm_config || false,
           sections: sectionsArray,
           lang_id: formData.lang_id || 1,
           format_json: formData.format_json || null,
           global_prompt: formData.global_prompt || null,
           frontpage: formData.frontpage || null,
           // Carry source_doc_id from a doc-import generate so the backend persists
           // each agent's imported response (persist_doc_import_responses runs only
           // when source_doc_id is present). Null for normal/library/magic creates.
           source_doc_id: formData.source_doc_id || null,
           heading_mapping_id: formData.heading_mapping_id || null,
         };
       }
       if (formData.vm_config) {
         if (!vmConfiguration || !vmConfiguration.stakeholder_requirement) {
           onError('Value Message configuration is enabled but the stakeholder requirement is missing.');
           setIsLoading(false);
           endProcessing();
           return;
         }
         requestData.vm_config_data = vmConfiguration;
       }
       
       console.log('Sending request data:', JSON.stringify(requestData, null, 2)); // Debug log
 
       const response = await axios.post(
         `${API_BASE_URL}${endpoint}`, 
         requestData, 
         { headers }
       );
 
       const result = response.data;
 
       if (result.status === 'NotAuthorized') {
         authNavigate('/login', 'iam', navigate);
         return;
       }
 
       if (result.status === 'failed') {
         onError(result.message);
         return;
       }
      initialFormDataRef.current = JSON.parse(JSON.stringify(formData));
       onSuccess(result.message, formData.lang_id, formData.template_name);
     } catch (error) {
       console.error('Error saving template:', error);
       onError(error.message || 'Failed to save template');
     } finally {
       setIsLoading(false);
       endProcessing();
     }
   };
 
   // Handle magic prompt generation
   const handleMagicGenerate = () => {
     if (!magicPrompt.trim()) {
       onError('Please enter a magic prompt');
       return;
     }
 
     if (!formData.fabric_id) {
       onError('Please select a project first');
       return;
     }
 
     // Check if user has existing sections/agents
     if (formData.sectionOrder.length > 0) {
       setShowMagicConfirm(true);
     } else {
       generateMagicTemplate();
     }
   };
     submitFnRef.current = () => handleSubmit({ preventDefault: () => {} });
   const handleValueMessageToggle = (e) => {
     const isEnabled = e.target.checked;
     setUseValueMessage(isEnabled);
     setFormData(prev => ({
       ...prev,
       vm_config: isEnabled
     }));
 
     if (isEnabled && !vmConfiguration) {
       const allDocIds = getAllFabricDocuments();
       setVmConfiguration({
         stakeholder_requirement: '',
         categories: [],
         selected_doc_ids: allDocIds.map(m => m.doc_id),
       });
     }
   };
   const openValueMessageModal = () => {
     if (!formData.fabric_id) {
       return;
     }
     setValueMessageModalOpen(true);
   };
   const handleSaveVmConfiguration = (config) => {
     setVmConfiguration(config);
     setValueMessageModalOpen(false);
   };
   const closeValueMessageModal = () => {
     setValueMessageModalOpen(false);
   };

  const getParagraphStyleOptions = () => {
    const paragraphConfigs = Array.isArray(formData.format_json?.paragraph)
      ? formData.format_json.paragraph
      : [];

    const customParagraphs = paragraphConfigs.filter(
      (p) => (p?.id || "default") !== "default"
    );

    return customParagraphs.map((p, index) => ({
      label: p.name || `Paragraph (Style ${index + 1})`,
      value: p.level ?? (index + 1),
    }));
  };

  const toggleParagraphDropdown = (agentKey) => {
    setOpenParagraphDropdown((prev) => (prev === agentKey ? null : agentKey));
  };

  const closeParagraphDropdown = () => {
    setOpenParagraphDropdown(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeParagraphDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
 
   // onfirm magic generation and replace existing content
   const handleConfirmMagicGenerate = () => {
     setShowMagicConfirm(false);
     generateMagicTemplate();
   };
 
   // Generate magic template
   const generateMagicTemplate = async () => {
     setIsMagicGenerating(true);
     
     beginProcessing('Your magic prompt is brewing your personalized template');
 
     try {
       const response = await axios.post(
         `${API_BASE_URL}/generate-magic-template`,
         {
           fabric_id: formData.fabric_id,
           magic_prompt: magicPrompt
         },
         { headers }
       );
 
       if (response.data.status === "success") {
         const generatedSections = response.data.data.sections;

         // Convert generated sections to dictionary format
         const { sectionsDict, sectionOrder } =
           convertSectionsToDict(generatedSections);

         // Update form data - replace existing sections
         setFormData((prev) => {
           // Preserve existing DB sections as deleted so backend knows to remove them
           const deletedSections = {};
           Object.entries(prev.sectionsDict).forEach(([key, section]) => {
             if (section.id) {
               deletedSections[key] = { ...section, isDeleted: true };
             }
           });

           return {
             ...prev,
             sectionsDict: { ...deletedSections, ...sectionsDict },
             sectionOrder, // only new sections in order, deleted ones excluded
             source_doc_id: null,
           };
         });

         // Clear the magic prompt
         setMagicPrompt("");

         // Magic-generated templates are not library imports — hide the
         // "Import from Document" follow-up which needs a library template id.
         setImportedLibraryTemplateId(null);
         setImportedLibraryTemplateName("");

         // Auto-populate all agents with fabric documents if available
         if (fabricTreeData.length > 0) {
           updateAllAgentsWithFabricDocuments();
         }
       } else {
         onError(response.data.message || "Failed to generate magic template");
       }
     } catch (error) {
       console.error('Error generating magic template:', error);
       onError('Failed to generate magic template. Please try again.');
     } finally {
       setIsMagicGenerating(false);
       endProcessing();
     }
   };

   const addTempIdsToSections = (sectionsArray) => {
    if (!sectionsArray || sectionsArray.length === 0) return [];

    const orderingIdToTempIdMap = {};
    const idToTempIdMap = {};
    const sectionsWithTempIds = [];

    // Pass 1: Create a fresh temp_id for each section and map BOTH its
    sectionsArray.forEach(section => {
      const temp_id = `temp_${Date.now()}_${Math.random()}`;
      if (section.section_ordering_id != null) {
        orderingIdToTempIdMap[String(section.section_ordering_id)] = temp_id;
      }
      if (section.id != null) {
        idToTempIdMap[String(section.id)] = temp_id;
      }
      sectionsWithTempIds.push({ ...section, temp_id });
    });

    // Pass 2: Rebuild parent linkage, strip stale DB ids, add agent temp_ids.
    return sectionsWithTempIds.map(section => {
      let parentTempId = null;
      const orderingId =
        section.section_ordering_id != null ? String(section.section_ordering_id) : '';
      if (orderingId.includes('.')) {
        const parentOrderingId = orderingId.split('.').slice(0, -1).join('.');
        parentTempId = orderingIdToTempIdMap[parentOrderingId] || null;
      }
      if (!parentTempId && section.parent_section_id != null) {
        const rawParent = String(section.parent_section_id);
        parentTempId =
          idToTempIdMap[rawParent] || orderingIdToTempIdMap[rawParent] || null;
      }

      const agentsWithTempIds = (section.agents || []).map(agent => {
        const { id: _staleAgentId, ...agentRest } = agent;
        return {
          ...agentRest,
          temp_id: `agent_temp_${Date.now()}_${Math.random()}`,
          par_style: agent.par_style ?? null
        };
      });
      const { id: _staleSectionId, ...sectionRest } = section;

      return {
        ...sectionRest,
        parent_section_id: parentTempId,
        agents: agentsWithTempIds
      };
    });
  };

  // ✅ UPDATED: performImport - sare fabric documents auto-select
  const performImport = ({
    sections: sectionsToImport,
    name: templateName,
    format_json: importedFormat,
    frontpage: importedFrontpage,
    global_prompt: importedGlobalPrompt = null,
    source_doc_id: importedSourceDocId = null,
    heading_mapping_id: importedHeadingMappingId = null,
    doc_generated: isDocGenerated = false,
    source_lang_id: importedSourceLangId = null,
    source_lang_name: importedSourceLangName = null,
  }) => {
    beginProcessing('Importing template structure...');

    // 1. Add temp_ids to the imported structure
    let sectionsWithTempIds = addTempIdsToSections(sectionsToImport);

    // 2. Tag-based doc mapping: if agent has tag, map only matching docs; otherwise all docs
    const allDocumentMappings = getAllFabricDocuments();

    sectionsWithTempIds = sectionsWithTempIds.map(section => ({
      ...section,
      agents: (section.agents || []).map(agent => {
        const agentMappings = agent.tag
          ? getAllFabricDocuments(agent.tag)
          : allDocumentMappings;
        return {
          ...agent,
          document_mappings: agentMappings.length > 0
            ? agentMappings
            : (agent.document_mappings || []),
          par_style: agent.par_style ?? null
        };
      })
    }));

    // 3. Convert to dictionary and update state
    const { sectionsDict, sectionOrder } = convertSectionsToDict(sectionsWithTempIds);

    if (importedSourceLangId) {
      setSelectedLanguage({
        id: importedSourceLangId,
        language_name:
          importedSourceLangName ||
          langsData?.[importedSourceLangId] ||
          `Language ${importedSourceLangId}`,
      });
    }

    setFormData(prev => ({
      ...prev,
      template_name: templateName,
      sectionsDict,
      sectionOrder,
      format_json: importedFormat || DEFAULT_REPORT_CONFIG,
      frontpage: importedFrontpage || null,
      global_prompt: importedGlobalPrompt || prev.global_prompt,
      source_doc_id: importedSourceDocId || null,
      heading_mapping_id: importedHeadingMappingId || null,
      ...(importedSourceLangId ? { lang_id: importedSourceLangId } : {}),
    }));

    endProcessing();
  };

  // ✅ UPDATED: handleTemplateImport - ignore source_doc_id
  const handleTemplateImport = (importedData) => {
    const isDocGenerated = !!importedData.doc_generated;

    if (formData.sectionOrder.length === 0) {
      setIsDocGeneratedImport(isDocGenerated);
      if (importedData.library_template_id) {
        setImportedLibraryTemplateId(importedData.library_template_id);
        setImportedLibraryTemplateName(importedData.name || '');
      }
      performImport(importedData);
    } else {
      setTemplateToImport({ ...importedData, doc_generated: isDocGenerated });
      setShowImportConfirm(true);
    }
  };

  // ✅ UPDATED: handleConfirmImport - ignore source_doc_id
  const handleConfirmImport = () => {
    if (templateToImport) {
      const isDocGenerated = !!templateToImport.doc_generated;
      setIsDocGeneratedImport(isDocGenerated);
      if (templateToImport.library_template_id) {
        setImportedLibraryTemplateId(templateToImport.library_template_id);
        setImportedLibraryTemplateName(templateToImport.name || '');
      }
      performImport(templateToImport);
    }
    setShowImportConfirm(false);
    setTemplateToImport(null);
  };

  const handleViewSavedMapping = async () => {
    if (!headingMappingId || isLoadingSavedMapping) return;
    setIsLoadingSavedMapping(true);
    beginProcessing('Loading section mapping');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/doc-import/heading-mapping/get`,
        { fabric_id: formData.fabric_id, heading_mapping_id: headingMappingId },
        { headers }
      );

      if (response.data?.status === 'success') {
        setSavedMapping(response.data);
      } else {
        onError(response.data?.message || 'Failed to load the saved section mapping');
      }
    } catch (error) {
      onError(
        error?.response?.data?.message ||
        error?.message ||
        'Failed to load the saved section mapping'
      );
    } finally {
      setIsLoadingSavedMapping(false);
      endProcessing();
    }
  };

 // Add a new S-series sub-section under an existing summary section
 const addSummarySubSection = (parentKey = null) => {
   const newKey = `temp_${Date.now()}_${Math.random()}`;

   // Count existing S-series root sections or children
   let newOrderingId;
   if (!parentKey) {
     const existingSCount = formData.sectionOrder.filter(key => {
       const sec = formData.sectionsDict[key];
       return sec && isSummarySection(sec) && !sec.parent_section_id;
     }).length;
     newOrderingId = `S${existingSCount + 1}`;
   } else {
     const parentSection = formData.sectionsDict[parentKey];
     const parentOid = parentSection?.section_ordering_id || 'S0';
     const childrenCount = formData.sectionOrder.filter(key => {
       const sec = formData.sectionsDict[key];
       return sec && sec.parent_section_id === parentKey;
     }).length;
     newOrderingId = `${parentOid}.${childrenCount + 1}`;
   }

   const insertPosition = findInsertionPosition(parentKey, formData.sectionOrder, formData.sectionsDict);

   const newSection = {
     temp_id: newKey,
     section_name: '',
     parent_section_id: parentKey,
     section_ordering_id: newOrderingId,
     is_summary_section: true,
     agentsDict: {},
     agentOrder: [],
     isNew: true
   };

   setFormData(prev => {
     const newSectionOrder = [...prev.sectionOrder];
     newSectionOrder.splice(insertPosition, 0, newKey);
     return {
       ...prev,
       sectionsDict: { ...prev.sectionsDict, [newKey]: newSection },
       sectionOrder: newSectionOrder
     };
   });
 };

 // Recursively assign S-prefix ordering IDs to a section and all its descendants
 const assignSPrefixRecursive = (parentKey, parentOrderingId, sectionsDict, sectionOrder) => {
   const children = sectionOrder.filter(key =>
     sectionsDict[key]?.parent_section_id === parentKey
   );
   children.forEach((childKey, idx) => {
     const childOrderingId = `${parentOrderingId}.${idx + 1}`;
     sectionsDict[childKey] = {
       ...sectionsDict[childKey],
       is_summary_section: true,
       section_ordering_id: childOrderingId,
       isModified: sectionsDict[childKey].id ? true : undefined
     };
     assignSPrefixRecursive(childKey, childOrderingId, sectionsDict, sectionOrder);
   });
 };

 // Recursively assign ordinary ordering IDs to a section and all its descendants
 const assignOrdinaryPrefixRecursive = (parentKey, parentOrderingId, sectionsDict, sectionOrder) => {
   const children = sectionOrder.filter(key =>
     sectionsDict[key]?.parent_section_id === parentKey
   );
   children.forEach((childKey, idx) => {
     const childOrderingId = `${parentOrderingId}.${idx + 1}`;
     sectionsDict[childKey] = {
       ...sectionsDict[childKey],
       is_summary_section: false,
       section_ordering_id: childOrderingId,
       isModified: sectionsDict[childKey].id ? true : undefined
     };
     assignOrdinaryPrefixRecursive(childKey, childOrderingId, sectionsDict, sectionOrder);
   });
 };

 // Toggle a root section between ordinary and special (S-series)
 const toggleSpecialSection = (sectionKey) => {
   const section = formData.sectionsDict[sectionKey];
   if (!section) return;

   // Show info popup once per editing session on first conversion to special
   if (!isSummarySection(section) && !specialInfoShownRef.current) {
     specialInfoShownRef.current = true;
     setShowSpecialSectionInfo(true);
   }

   setFormData(prev => {
     const section = prev.sectionsDict[sectionKey];
     if (!section) return prev;

     let updatedSectionsDict = { ...prev.sectionsDict };
     let updatedSectionOrder = [...prev.sectionOrder];
     const descendants = findAllDescendants(sectionKey, updatedSectionsDict, updatedSectionOrder);

     if (!isSummarySection(section)) {
       // ORDINARY → SPECIAL
       // 1. Mark parent as summary with temporary S-prefix
       updatedSectionsDict[sectionKey] = {
         ...section,
         is_summary_section: true,
         section_ordering_id: 'S999',
         isModified: section.id ? true : undefined
       };

       // 2. Recursively mark and assign temp S-prefix ordering for all descendants
       assignSPrefixRecursive(sectionKey, 'S999', updatedSectionsDict, updatedSectionOrder);

       // 3. Move parent + all descendants to the end of S-series block
       const group = [sectionKey, ...descendants];
       updatedSectionOrder = updatedSectionOrder.filter(key => !group.includes(key));
       let insertIdx = 0;
       for (let i = 0; i < updatedSectionOrder.length; i++) {
         const sec = updatedSectionsDict[updatedSectionOrder[i]];
         if (sec && isSummarySection(sec)) {
           insertIdx = i + 1;
         } else {
           break;
         }
       }
       updatedSectionOrder.splice(insertIdx, 0, ...group);

       // 4. Recalculate all S-series numbering — assigns correct S1, S2... and their nested children
       updatedSectionsDict = recalculateSummaryOrderingIds(updatedSectionsDict, updatedSectionOrder);
       // 5. Deep-renumber children for each S-root (recalculateSummaryOrderingIds only does 1 level)
       const sRoots = updatedSectionOrder.filter(key => {
         const sec = updatedSectionsDict[key];
         return sec && isSummarySection(sec) && !sec.parent_section_id;
       });
       sRoots.forEach(rootKey => {
         assignSPrefixRecursive(rootKey, updatedSectionsDict[rootKey].section_ordering_id, updatedSectionsDict, updatedSectionOrder);
       });
       // 6. Recalculate ordinary sections numbering (remaining sections shift up)
       updatedSectionsDict = recalculateSiblingOrderingIds(null, updatedSectionsDict, updatedSectionOrder);

     } else {
       // SPECIAL → ORDINARY
       // 1. Unmark parent with temporary ordering
       updatedSectionsDict[sectionKey] = {
         ...section,
         is_summary_section: false,
         section_ordering_id: '0',
         isModified: section.id ? true : undefined
       };

       // 2. Recursively unmark and assign temporary ordering to all descendants at any depth
       assignOrdinaryPrefixRecursive(sectionKey, '0', updatedSectionsDict, updatedSectionOrder);

       // 3. Move parent + all descendants to be the first ordinary section (right after S-series)
       const group = [sectionKey, ...descendants];
       updatedSectionOrder = updatedSectionOrder.filter(key => !group.includes(key));
       let insertIdx = 0;
       for (let i = 0; i < updatedSectionOrder.length; i++) {
         const sec = updatedSectionsDict[updatedSectionOrder[i]];
         if (sec && isSummarySection(sec)) {
           insertIdx = i + 1;
         } else {
           break;
         }
       }
       updatedSectionOrder.splice(insertIdx, 0, ...group);

       // 4. Recalculate remaining S-series numbering (S2→S1, S3→S2...)
       updatedSectionsDict = recalculateSummaryOrderingIds(updatedSectionsDict, updatedSectionOrder);
       // 5. Deep-renumber children for each remaining S-root
       const sRoots = updatedSectionOrder.filter(key => {
         const sec = updatedSectionsDict[key];
         return sec && isSummarySection(sec) && !sec.parent_section_id;
       });
       sRoots.forEach(rootKey => {
         assignSPrefixRecursive(rootKey, updatedSectionsDict[rootKey].section_ordering_id, updatedSectionsDict, updatedSectionOrder);
       });
       // 6. Recalculate all ordinary sections numbering (this becomes first, rest shift)
       updatedSectionsDict = recalculateSiblingOrderingIds(null, updatedSectionsDict, updatedSectionOrder);
     }

     return {
       ...prev,
       sectionsDict: updatedSectionsDict,
       sectionOrder: updatedSectionOrder
     };
   });
 };

 // Renders the imported-response toggle+panel for a single agent.
 // Used in both renderSummarySection and renderSection 
 const renderImportedResponse = (agentKey, agent) => {
   if (editingTemplate || !isDocGeneratedImport || !agent.parsed_response) return null;
   return (
     <div className="mt-1">
       <button
         type="button"
         className={`ev-btn-xsmall ev-fs-11 ev-bold-reg ${showResponsePanel[agentKey] ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
         onClick={() => setShowResponsePanel(prev => ({ ...prev, [agentKey]: !prev[agentKey] }))}
       >
         <i className={`bi ${showResponsePanel[agentKey] ? 'bi-eye-slash' : 'bi-eye'} me-1`}></i>
         {showResponsePanel[agentKey] ? 'Hide Imported Response' : 'Show Imported Response'}
       </button>
       {showResponsePanel[agentKey] && (
         <div className="imported-response-panel ev-message-card2 ev-radius-xsmall pb-3 pt-0 mt-1">
           <GenbiResponseComponent
             agent={{
               id: agentKey,
               agent_name: agent.agent_name,
               response: {
                 ...agent.parsed_response,
                 images: (agent.parsed_response?.images || []).map(img => ({
                   ...img,
                   presigned_url: img.preview_url || img.presigned_url || null,
                 })),
               },
               editedResponse: null,
               status: 'COMPLETED',
             }}
             status="COMPLETED"
             isLoading={false}
             fabricId={formData.fabric_id}
             authToken={GetCookie('access_token')}
             compact={true}
           />
         </div>
       )}
     </div>
   );
 };

 // Render S-series section in locked-down mode (no reorder, no delete, only edit prompts + add)
 const renderSummarySection = (sectionKey, compact = false) => {
   const section = formData.sectionsDict[sectionKey];
   if (!section) return null;

   const oid = section.section_ordering_id || 'S';
   const level = oid.includes('.') ? oid.split('.').length - 1 : 0;
   const indentPx = level * 15;

   if (compact && level > COMPACT_MAX_SECTION_LEVEL) return null;

   return (
     <div key={sectionKey} className="special-section-glow mb-2">
       <div className="d-flex align-items-center mb-1">
         <div className="d-flex" >
           <span className="ev-message-card3 me-2 ms-1 mt-1 template-section-order-card d-flex ev-radius-xsmall align-items-center ev-fs-11">
             <i className="bi bi-journal-text me-1"></i>{oid}
           </span>
         </div>
         <div style={{ width: '65%' }}>
           <input
             type="text"
             className="formField4 ev-btn-small ev-fs-12 ev-radius-xsmall mt-1"
             placeholder="Section Name"
             value={section.section_name}
             onChange={(e) => updateSectionField(sectionKey, 'section_name', e.target.value)}
           />
         </div>
         <div className="col-auto ms-2 d-flex align-items-center">
           {!section.parent_section_id && (
             <div className="ms-1 ev-message-card2 me-1 template-caret-card">
               <button
                 type="button"
                 className="template-ordering-btn"
                 onClick={() => moveSectionOrder(sectionKey, 'up')}
                 disabled={!canMoveSectionUp(sectionKey)}
               >
                 <i className="bi bi-caret-up-square"></i>
               </button>
               <button
                 type="button"
                 className="template-ordering-btn ms-1"
                 onClick={() => moveSectionOrder(sectionKey, 'down')}
                 disabled={!canMoveSectionDown(sectionKey)}
               >
                 <i className="bi bi-caret-down-square"></i>
               </button>
             </div>
           )}
           <button
             type="button"
             className="ev-btn-red-fill-round ev-icon-btn-padding-small ev-radius-xsmall ev-bold-reg ms-1"
             onClick={() => deleteSection(sectionKey)}
           >
             <i className="bi bi-trash"></i>
           </button>
           <button
             type="button"
             className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg ev-fs-11 ms-1"
             onClick={() => addSummarySubSection(sectionKey)}
           >
             <i className="bi bi-plus-circle-fill"></i> Sub-section
           </button>
           <button
             type="button"
             className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg ev-fs-11 ms-1"
             onClick={() => addAgent(sectionKey)}
           >
             <i className="bi bi-plus-circle-fill"></i> Agent
           </button>
           {!section.parent_section_id && (
             <button
               type="button"
               className="special-section-toggle-btn active ms-2"
               onClick={() => toggleSpecialSection(sectionKey)}
               title="Remove Special Section"
             >
               <i className="bi bi-star-fill"></i> Special
             </button>
           )}
           {section.is_summary_section && (
             <div
               ref={specialSectionConfigRef}
               className="position-relative d-inline-block ms-2"
             >
               <button
                 type="button"
                 className="no-wrap ev-btn-lightblue-fill-round ev-btn-small ev-bold-reg border-0 d-flex align-items-center"
                 onClick={(e) => {
                   if (openSpecialSectionConfig?.key === sectionKey) {
                     setOpenSpecialSectionConfig(null);
                   } else {
                     const rect = e.currentTarget.getBoundingClientRect();
                     setOpenSpecialSectionConfig({ key: sectionKey, top: rect.bottom + 4, right: document.documentElement.clientWidth - rect.right });
                   }
                 }}
               >
                 <i className="bi bi-bounding-box me-1" />
                 Config
                 <i className="bi bi-chevron-down ms-1" />
               </button>
             </div>
           )}
         </div>
       </div>

       {/* Section Config dropdown rendered outside overflow container */}
       {openSpecialSectionConfig?.key === sectionKey && (() => {
         const ssc = section.special_section_config || {};
         return ReactDOM.createPortal(
           <div
             ref={specialSectionConfigRef}
             className="ev-card-4 ev-message-card2 d-flex flex-column gap-1 p-2 rounded-2"
             style={{ position: 'fixed', top: openSpecialSectionConfig.top, right: openSpecialSectionConfig.right, minWidth: '220px', zIndex: 10000 }}
           >
             <span className="ev-small-p-blue ev-bold-med px-2 pt-1 pb-1 text-start text-nowrap">Section Config</span>

             {/* Outside Border */}
             <div className="ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25">
               <span className="d-flex align-items-center gap-2 text-nowrap">
                 <i className="bi bi-box-arrow-up-right" />
                 <span>Outside Border</span>
               </span>
               <div
                 className={`agent-lock-toggle agent-lock-toggle--sm flex-shrink-0 ${ssc.is_outside_border ? 'agent-lock-toggle--on' : ''}`}
                 onClick={() => updateSpecialSectionConfig(sectionKey, 'is_outside_border', !ssc.is_outside_border)}
               >
                 <div className="agent-lock-toggle-knob" />
               </div>
             </div>

             {/* Heading BG Color */}
             <div className="ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-0 rounded-2 border border-secondary border-opacity-25" style={{ minHeight: 0 }}>
               <span className="d-flex align-items-center gap-2 text-nowrap">
                 <i className="bi bi-paint-bucket" />
                 <span>Heading BG</span>
               </span>
               <span className="d-flex align-items-center" style={{ transform: 'scale(0.85)', transformOrigin: 'right center' }}>
               <ColorPickerButton
                 value={ssc.heading_bg_color || '#FFFFFF'}
                 onChange={(color) => updateSpecialSectionConfig(sectionKey, 'heading_bg_color', color)}
                 icon={<i className="bi bi-square-fill" />}
               />
               </span>
             </div>
           </div>,
           document.body
         );
       })()}

       {/* Agents for this summary section */}
       {!compact && section.agentOrder?.map((agentKey) => {
         const agent = section.agentsDict[agentKey];
         if (!agent) return null;

         return (
           <div key={agentKey} className="d-flex align-items-start mt-1 mb-1">
             <div style={{ marginLeft: `${indentPx + 15}px`, width: '65%' }}>
               <div className="ev-card-8 ev-radius-small ev-padding-small">
                 <div className="row mb-1">
                   <div className="col-6">
                     <span className="ev-message-card3 ev-btn-xsmall ev-fs-11 ms-0 mt-0">
                       <i className="bi bi-robot"></i> S.{oid}. Agent {agent.agent_ordering_id}
                     </span>
                   </div>
                   <div className="col-6 text-end">
                     <button
                       type="button"
                       className="ev-btn-blue-fill-round ms-1 ev-bold-reg ev-btn-small text-nowrap"
                       onClick={() => openConfigureModal(sectionKey, agentKey)}
                     >
                       <i className="bi bi-gear-wide"></i> Configure
                     </button>
                     <div className="position-relative d-inline-block ms-1">
                       <TableStructureSelector
                         sectionKey={sectionKey}
                         agentKey={agentKey}
                         selectedId={agent.table_structure_id}
                         onSelect={(id) => updateAgentField(sectionKey, agentKey, 'table_structure_id', id)}
                         structures={tableStructures}
                       />
                     </div>
                   </div>
                 </div>
                 <input
                   type="text"
                   className="formField4 ev-btn-xsmall ev-fs-12 ev-radius-xsmall mb-2"
                   placeholder="Name"
                   value={agent.agent_name}
                   onChange={(e) => updateAgentField(sectionKey, agentKey, 'agent_name', e.target.value)}
                 />
                 <div className="btn-group custom-btn-group-outer mb-1" role="group">
                   <button
                     type="button"
                     className={`btn genbi-custom-btn-group-inner prompt-toggle-sm ${(!activePromptTab[agentKey] || activePromptTab[agentKey] === 'prompt') ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                     onClick={() => setActivePromptTab(prev => ({ ...prev, [agentKey]: 'prompt' }))}
                   >
                     Prompt
                   </button>
                   <button
                     type="button"
                     className={`btn genbi-custom-btn-group-inner prompt-toggle-sm ${activePromptTab[agentKey] === 'additional_context' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                     onClick={() => setActivePromptTab(prev => ({ ...prev, [agentKey]: 'additional_context' }))}
                   >
                     Additional Context
                   </button>
                 </div>
                 <div className="mb-0">
                   {(!activePromptTab[agentKey] || activePromptTab[agentKey] === 'prompt') ? (
                     <textarea
                       className="formField3 ev-padding-xsmall ev-fs-12"
                       placeholder="Provide context here"
                       value={agent.agent_context || ''}
                       onChange={(e) => updateAgentField(sectionKey, agentKey, 'agent_context', e.target.value)}
                     />
                   ) : (
                     <textarea
                       className="formField3 ev-padding-xsmall ev-fs-12"
                       placeholder="Provide additional context here (optional)"
                       value={agent.additional_context || ''}
                       onChange={(e) => updateAgentField(sectionKey, agentKey, 'additional_context', e.target.value)}
                     />
                   )}
                 </div>
                 {renderImportedResponse(agentKey, agent)}
               </div>
             </div>
           </div>
         );
       })}

       {/* Render child summary sections recursively */}
       {formData.sectionOrder
         .filter(key => formData.sectionsDict[key]?.parent_section_id === sectionKey)
         .map(childKey => renderSummarySection(childKey, compact))
       }
     </div>
   );
 };

 // Render section with hierarchy
 const renderSection = (sectionKey, compact = false) => {
   const section = formData.sectionsDict[sectionKey];
   if (!section) return null;

   // S-series sections are rendered separately in their own container
   if (isSummarySection(section)) return null;
 
   const level = (section.section_ordering_id?.split('.').length || 1) - 1;
   const indentPx = level * 15;

   if (compact && level > COMPACT_MAX_SECTION_LEVEL) return null;

   // Calculate dynamic widths based on nesting level
   const baseIndentWidth = 5; // Base width percentage for indent
   const indentWidthPercent = baseIndentWidth + (level * 0.7); // Increase indent width by 2% per level
   const sectionWidthPercent = 70 - (level * 0.7); // Decrease section width by 2% per level
   
   const indentStyle = { 
     marginLeft: `${indentPx}px`, 
     minWidth: `${indentWidthPercent}%`, 
     textAlign: "right" 
   };
 
   return (
     <div key={sectionKey}>
       <div className="d-flex align-items-center justify-content-center mb-1">
         <div className="d-flex" style={{ marginLeft: `${indentPx}px`, minWidth: `${indentWidthPercent}%`, textAlign: "right" }}>
           <span className="ev-message-card2 me-1 ms-2 mt-1 template-section-order-card d-flex align-items-center" >
             {section.section_ordering_id}
           </span>
         </div>
         <div style={{ width: `${sectionWidthPercent}%` }}>
           <input
             type="text"
             className="formField4 ev-btn-small ev-fs-12 ev-radius-xsmall template-viewer-sec-pd-right-2btn mt-1"
             placeholder="Section Name"
             value={section.section_name}
             onChange={(e) => updateSectionField(sectionKey, 'section_name', e.target.value)}
           />
         </div>
         <div className="col-auto d-flex align-items-center">
           <div className="ev-message-card2 me-1 ms-2 template-caret-card">
             <button
               type="button"
               className="template-ordering-btn"
               onClick={() => moveSectionOrder(sectionKey, 'up')}
               disabled={!canMoveSectionUp(sectionKey)}
             >
             <i className="bi bi-caret-up-square"></i>
             </button>
             <button
               type="button"
               className="template-ordering-btn ms-1"
               onClick={() => moveSectionOrder(sectionKey, 'down')}
               disabled={!canMoveSectionDown(sectionKey)}
             >
               <i className="bi bi-caret-down-square"></i>
             </button>
           </div>
           <button
             type="button"
             className="ev-btn-red-fill-round ev-icon-btn-padding-small ev-radius-xsmall ev-bold-reg  mt-0 ms-1"
             onClick={() => deleteSection(sectionKey)}
           >
             <i className="bi bi-trash"></i>
           </button>
           <button
             type="button"
             className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg  ms-3"
             onClick={() => addSection(sectionKey)}
           >
             <i className="bi bi-plus-circle-fill"></i> Section
           </button>
           <button
             type="button"
             className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg ms-1"
             onClick={() => addAgent(sectionKey)}
           >
             <i className="bi bi-plus-circle-fill"></i> AI Agent
           </button>
           {!section.parent_section_id && (
             <button
               type="button"
               className="special-section-toggle-btn ms-2"
               onClick={() => toggleSpecialSection(sectionKey)}
               title="Make Special Section"
             >
               <i className="bi bi-star"></i> Special
             </button>
           )}
         </div>
       </div>

       {/* Render agents for this section */}
       {!compact && section.agentOrder?.map((agentKey) => {
         const agent = section.agentsDict[agentKey];
         if (!agent) return null;
 
         const agentIndex = section.agentOrder.indexOf(agentKey);
         const paragraphStyleOptions = getParagraphStyleOptions();
         const showParagraphStyleButton = paragraphStyleOptions.length > 0;
 
         return (
           <div key={agentKey}>
             <div className="d-flex align-items-start justify-content-center mt-1 mb-1">
               <div className="d-flex" style={{ marginLeft: `${indentPx}px`, minWidth: `${indentWidthPercent}%` }}></div>
               <div style={{ width: `${sectionWidthPercent}%` }}>
                 <div className="ev-card-8 ev-radius-small ev-padding-small">
                   <div className ="row mb-1">
                     <div className ="col-6">
                       <span className ="ev-message-card3 ev-btn-xsmall ev-fs-11 ms-0 mt-0"><i className ="bi bi-robot"></i> S.{section.section_ordering_id}. Agent {agent.agent_ordering_id} </span>
                     </div>
                     <div className="col-6 text-end d-flex align-items-center justify-content-end ">{showParagraphStyleButton && (
                       <div
                         ref={openParagraphDropdown === agentKey ? dropdownRef : null}
                         className="position-relative ms-1 d-inline-block"
                       >
                         <button
                           type="button"
                           className="ev-btn-lightblue-fill-round ev-btn-small ev-bold-reg ev-fs-11"
                           onClick={() => toggleParagraphDropdown(agentKey)}
                         >
                           <i className="bi bi-text-paragraph me-1"></i>
                           Style
                           <i className="bi bi-chevron-down ms-1"></i>
                         </button>
                            {openParagraphDropdown === agentKey && (
                              <div className="position-absolute top-100 end-0 mt-1 ev-card-4 ev-message-card2 d-flex flex-column gap-1 z-1000">
                                {paragraphStyleOptions.map((style) => {
                                  const isSelected = String(agent.par_style ?? "") === String(style.value ?? "");

                                  return (
                                    <button
                                      key={style.value ?? "default"}
                                      type="button"
                                      className={`par-style-item d-flex align-items-center ev-fs-12 ${
                                        isSelected ? "active" : ""
                                      }`}
                                      onClick={() => {
                                        const newValue = isSelected ? null : style.value;
                                        updateAgentField(sectionKey, agentKey, "par_style", newValue);
                                        closeParagraphDropdown();
                                      }}
                                    >
                                      <i
                                        className={`bi ${
                                          isSelected ? "bi-check-circle-fill" : "bi-circle"
                                        } me-2`}
                                      ></i>
                                      {style.label}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                       </div>
                     )}
                       <button
                         type="button"
                         className="ev-btn-blue-fill-round ms-1 ev-bold-reg ev-btn-small avatar-tooltip text-nowrap"
                         onClick={() =>
                           openConfigureModal(sectionKey, agentKey)
                         }
                       >
                         <i className="bi bi-gear-wide"></i> Configure
                       </button>
                       <div className="position-relative d-inline-block ms-1">
                        <TableStructureSelector
                          sectionKey={sectionKey}
                          agentKey={agentKey}
                          selectedId={agent.table_structure_id}
                          onSelect={(id) => updateAgentField(sectionKey, agentKey, 'table_structure_id', id)}
                          structures={tableStructures}
                        />
                      </div>
                     </div>
                   </div>
                   <input
                     type="text"
                     className="formField4 ev-btn-xsmall ev-fs-12 ev-radius-xsmall mb-2"
                     placeholder="Name"
                     value={agent.agent_name}
                     onChange={(e) => updateAgentField(sectionKey, agentKey, 'agent_name', e.target.value)}
                   />
                    <div className="btn-group custom-btn-group-outer mb-1" role="group">
                     <button
                       type="button"
                       className={`btn genbi-custom-btn-group-inner prompt-toggle-sm ${(!activePromptTab[agentKey] || activePromptTab[agentKey] === 'prompt') ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                       onClick={() => setActivePromptTab(prev => ({ ...prev, [agentKey]: 'prompt' }))}
                     >
                       Prompt
                     </button>
                     <button
                       type="button"
                       className={`btn genbi-custom-btn-group-inner prompt-toggle-sm ${activePromptTab[agentKey] === 'additional_context' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                       onClick={() => setActivePromptTab(prev => ({ ...prev, [agentKey]: 'additional_context' }))}
                     >
                       Additional Context
                     </button>
                   </div>
                   <div className="mb-0">
                     {(!activePromptTab[agentKey] || activePromptTab[agentKey] === 'prompt') ? (
                       <textarea
                         className="formField3 ev-padding-xsmall ev-fs-12"
                         placeholder="Provide context here"
                         value={agent.agent_context || ''}
                         onChange={(e) => updateAgentField(sectionKey, agentKey, 'agent_context', e.target.value)}
                       />
                     ) : (
                       <textarea
                         className="formField3 ev-padding-xsmall ev-fs-12"
                         placeholder="Provide additional context here (optional)"
                         value={agent.additional_context || ''}
                         onChange={(e) => updateAgentField(sectionKey, agentKey, 'additional_context', e.target.value)}
                       />
                     )}
                   </div>

                   {/* Show Response toggle — only in create mode after doc-generate import */}
                   {renderImportedResponse(agentKey, agent)}
                 </div>
               </div>
 
               <div className="col-4">
                 <div className="ev-message-card2 me-1 ms-2  template-caret-card" >
                   <button
                     type="button"
                     className="template-ordering-btn"
                     onClick={() => moveAgentOrder(sectionKey, agentKey, 'up')}
                     disabled={agentIndex === 0}
                   >
                   <i className="bi bi-caret-up-square"></i>
                   </button>
                   <button
                     type="button"
                     className="template-ordering-btn ms-1"
                     onClick={() => moveAgentOrder(sectionKey, agentKey, 'down')}
                     disabled={agentIndex === section.agentOrder.length - 1}
                   >
                     <i className="bi bi-caret-down-square"></i>
                   </button>
                 </div>
                 
                 <button
                   type="button"
                   className="ev-btn-red-fill-round ev-icon-btn-padding-small ev-radius-xsmall  ms-1"
                   onClick={() => deleteAgent(sectionKey, agentKey)}
                 >
                   <i className="bi bi-trash"></i>
                 </button>
               </div>
             </div>
           </div>
         );
       })}
     </div>
   );
 };

 const getMagicConfirmBodyContent = () => {
  return (
    <div>
      <div className="alert alert-warning" role="alert">
        <i className="bi bi-exclamation-triangle me-2"></i>
        <strong>Warning:</strong> You have existing sections and agents in your template.
      </div>
      <p>Generating a magic template will <strong>replace all</strong> your current sections and agents...</p>
      <div className="mb-3 p-2 bg-light rounded">
        <strong>Prompt:</strong> "{magicPrompt}"
      </div>
      <p className="mb-2"><strong>Current template contains:</strong></p>
      <ul className="mb-3">
        <li>{formData.sectionOrder.length} section(s)</li>
        <li>{Object.values(formData.sectionsDict).reduce((total, section) => total + (section.agentOrder?.length || 0), 0)} agent(s)</li>
      </ul>
      <p className="text-danger mb-0">This action cannot be undone. All your current work will be replaced.</p>
    </div>
  );
};

const getImportConfirmBodyContent = () => {
    return (
      <div>
        <div className="alert alert-warning" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Warning:</strong> You have existing sections and agents in your template.
        </div>
        <p>Importing a new template will <strong>replace all</strong> of your current work.</p>
        <p className="text-danger mb-0">This action cannot be undone.</p>
      </div>
    );
  };

   const getFabricSwitchConfirmBodyContent = () => {
     return (
       <div>
         <div className="alert alert-warning" role="alert">
           <i className="bi bi-exclamation-triangle me-2"></i>
           <strong>Warning:</strong> You have an unsaved generated template.
         </div>
         <p>Switching to a different project will discard all generated sections and agents.</p>
         <p className="mb-2"><strong>Current template contains:</strong></p>
         <ul className="mb-3">
           <li>{formData.sectionOrder.length} section(s)</li>
           <li>{Object.values(formData.sectionsDict).reduce((total, section) => total + (section.agentOrder?.length || 0), 0)} agent(s)</li>
         </ul>
        <p className="text-danger mb-0">This action cannot be undone.</p>
      </div>
    );
  };

return (
 <div className="ev-card-2 w-100 ev-padding-med vh-85 w-100 mt-3 scrollable-element">
   {/* THIS SLR INDICATOR right after opening div */}
    {fromSLR && (
      <div className="ev-alert-card-info mb-3">
        <i className="bi bi-info-circle me-2"></i>
        <strong>Creating from SLR:</strong> Template structure has been pre-loaded with included documents. 
        Review and click "Create Template" to finalize.
      </div>
    )}
    
    {/* Show template ID reference if available */}
    {preloadedTemplate?.templateId && (
      <input type="hidden" value={preloadedTemplate.templateId} />
    )}
 {/* Header with Create Template and Controls */}
<div className="row mb-1 align-items-center">
  <div className="col-auto">
    <p className="ev-med-p-blue mb-1">
      {editingTemplate ? 'Edit Template' : 'Create Template'}
    </p>
  </div>
  <div className="col mb-2">
    <div className="ev-card-8  ev-radius-large d-flex justify-content-between align-items-center ev-padding-xsmall">
      <button
      className="ev-btn-blue-outline-round ev-btn-small ms-1"
      onClick={openReportFormatting}
    >
      <i className="bi bi-file-earmark-text-fill"></i> Report Formatting
    </button>

      {/* Toggle Switch */}
      <div className="ev-message-card3 ev-btn-small d-flex align-items-center pe-1">
          <div className="d-flex  align-items-center">
            <div className="form-switch">
                <input
                  className="form-check-input custom-switch"
                  type="checkbox"
                  id="valueMessageToggle"
                  checked={useValueMessage}
                  onChange={handleValueMessageToggle}
                />
              </div>
              <span className=" ms-2">
                Include Value Messages
              </span>
                  <button
                    type="button"
                    className={`ev-btn-blue-fill-round ms-3 ev-bold-reg ev-btn-small avatar-tooltip text-nowrap`}
                    onClick={openValueMessageModal}
                    disabled={!useValueMessage || !formData.fabric_id}
                    data-tooltip={
                      !useValueMessage 
                        ? 'Please check "Include Value Messages" first to configure' 
                        : !formData.fabric_id 
                          ? 'Please select a project' 
                          : undefined
                    }
                  >
                  <i className ="bi bi-gear-wide"></i> Configure
                </button>
           </div>
      </div>
      
      {/* Language Selector */}
      <div className="col-4 me-1 ">
        <LanguageSelector
          options={langsData}
          isLoading={isLoadingLangs}
          selectedLanguage={selectedLanguage}
          onLanguageSelect={(langObj) => handleTranslateSelect(langObj.id, langObj.language_name)}
        />
      </div>
    </div>
  </div>
</div>
 
       <form onSubmit={handleSubmit}>
         <div className="row mb-2">
           <div className="col-6">
             <div className="form-group mb-1">
               <label className="ev-reg-p-dark mb-1">Template Name <span className="text-danger">*</span></label>
               <input
                 type="text"
                 className="formField3"
                 value={formData.template_name}
                 onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                 required
                 placeholder="Enter template name"
                 disabled={isLoading}
               />
             </div>
           </div>
 
           <div className="col-6">
             <div className="form-group mb-1">
               <label className="ev-reg-p-dark mb-1">Project Selection <span className="text-danger">*</span></label>
               <FabricSelector
                  selectedFabricId={formData.fabric_id}
                  selectedFabricName={formData.fabric_name}
                  onFabricSelect={handleFabricSelection}
                  disabled={!!editingTemplate}
                  fabricselectorapi={editingTemplate ? null : `${API_BASE_URL}/fabric_unstructured_data_tree`} // Add this line
                  closeOnOutsideClick={true}
               />
             </div>
           </div>
         </div>

         <div className="mb-2">
           {/* Global Prompt */}
           <label className="ev-reg-p-dark mb-1">
             Global Prompt <span className="ev-small-p-blue"><i>(Optional — prepended to every agent's context)</i></span>
           </label>
           <textarea
             className="formField3 ev-fs-12 px-3 py-2"
             rows={2}
             placeholder="e.g., Focus only on RCT evidence, use APA citation style, write in formal academic tone..."
             value={formData.global_prompt}
             onChange={(e) => setFormData(prev => ({ ...prev, global_prompt: e.target.value }))}
           />
         </div>
 
         <div className="mb-2">
           {/* Magic Prompt */}
           <label className="ev-reg-p-dark mb-1">Magic Template Prompt <span className="ev-small-p-blue"><i>(Optional)</i></span></label>
           <div className="ev-card-8 mb-2 p-1  ev-radius-small">
             <div className="form-group mb-1">
               <input 
                 type="text" 
                 className="formField3" 
                 placeholder="Auto create sections & agents"
                 value={magicPrompt}
                 onChange={(e) => setMagicPrompt(e.target.value)}
               />
             </div>
             <div className="text-end">
               <button 
                 type="button"
                 className="ev-btn-gradient-fill-round avatar-tooltip ev-radius-xsmall ev-bold-reg ev-btn-small"
                 onClick={handleMagicGenerate}
                 data-tooltip={!formData.fabric_id ? "Please select a project" : !magicPrompt.trim() ? "Please enter a prompt" : undefined}
                 disabled={!formData.fabric_id || !magicPrompt.trim() || isLoading || isMagicGenerating}
               >
                 {isMagicGenerating ? (
                   <>
                     {/* <div className="spinner-border spinner-border-sm me-1" role="status">
                       <span className="visually-hidden">Loading...</span>
                     </div> */}
                     Generating
                   </>
                 ) : (
                   <>
                     <i className="bi bi-play-fill"></i> Generate
                   </>
                 )}
               </button>
             </div>
           </div>
 
            {/* Sections Management */}
               {formData.sectionOrder.length === 0 && (
                 <div className="ev-alert-card-warning mt-2 mb-2">
                   <p className="mb-0">
                     <i className="bi bi-info-circle me-1"></i>
                     &nbsp;
                     {formData.fabric_id ? 'Click "Super Section" to add your first section or use the Magic Prompt above' : 'Select a project first'}
                   </p>
                 </div>
               )}
 
             <div className="ev-card-3">
              <div className="d-flex align-items-center">
              <button 
                type="button"
                className="ev-btn-blue-fill-round ev-bold-reg avatar-tooltip"
                onClick={() => addSection()}
                data-tooltip={!formData.fabric_id ? "Please select a project" : undefined}
                disabled={!formData.fabric_id}
              >
                <i className="bi bi-plus-circle"></i> Super Section
              </button>

              

              {!editingTemplate && (
                <div className="position-relative">
                  <button 
                    type="button"
                    className="ev-btn-lightblue-fill-round ev-bold-reg avatar-tooltip ms-2"
                    onClick={() => setIsTemplateLibraryOpen(!isTemplateLibraryOpen)}
                    // data-tooltip={!formData.fabric_id ? "Please select a fabric" : "Import a pre-defined template"}
                    // disabled={!formData.fabric_id}
                  >
                    <i className="bi bi-box-arrow-in-down"></i> Import Template
                  </button>

                  <GenbiTemplateLibraryList
                    isOpen={isTemplateLibraryOpen}
                    onClose={() => setIsTemplateLibraryOpen(false)}
                    onImport={handleTemplateImport}
                    onError={onError}
                    onProcessingStart={beginProcessing}
                    onProcessingEnd={endProcessing}
                  />
                </div>
              )}


              {!editingTemplate && importedLibraryTemplateId && (
                <div className="position-relative ms-2">
                  <button
                    type="button"
                    className="ev-btn-gradient-fill-round ev-bold-reg avatar-tooltip"
                    data-tooltip="Auto-build sections & agents from an existing document"
                    onClick={() => setIsImportFromDocOpen(!isImportFromDocOpen)}
                  >
                    <i className="bi bi-magic"></i> Build from Document
                  </button>

                  <ImportFromDocumentModal
                    isOpen={isImportFromDocOpen}
                    onClose={() => setIsImportFromDocOpen(false)}
                    onImport={handleTemplateImport}
                    onError={onError}
                    fabricId={formData.fabric_id}
                    templateName={formData.template_name}
                    libraryTemplateId={importedLibraryTemplateId}
                    libraryTemplateName={importedLibraryTemplateName}
                    onProcessingStart={beginProcessing}
                    onProcessingEnd={endProcessing}
                    onFullScreenChange={handleImportFullScreenChange}
                  />
                </div>
              )}

              {editingTemplate && headingMappingId && (
                <>
                  <button
                    type="button"
                    className="ev-btn-lightblue-fill-round ev-bold-reg avatar-tooltip ms-2"
                    data-tooltip="View the document-to-template section mapping this template was built from"
                    disabled={isLoadingSavedMapping}
                    onClick={handleViewSavedMapping}
                  >
                    <i className="bi bi-diagram-3"></i> Section Mapping
                  </button>

                  <GenbiHeadingMappingModal
                    isOpen={!!savedMapping}
                    mappingData={savedMapping}
                    docName={savedMapping?.doc_name || ''}
                    libraryTemplateName={savedMapping?.library_template_name || ''}
                    onCancel={() => setSavedMapping(null)}
                    readOnly
                  />
                </>
              )}
            </div>

                 {/* All sections in unified scroll — special (with glow) first, then ordinary */}
                 <div className={`scrollable-element mt-2 ${formData.sectionOrder.length === 0 ? "vh-25" : "vh-30"}`}>
                   {formData.sectionOrder
                     .filter(key => {
                       const sec = formData.sectionsDict[key];
                       return sec && isSummarySection(sec) && !sec.parent_section_id;
                     })
                     .map(key => renderSummarySection(key, isSectionTreeCompact))
                   }
                   {formData.sectionOrder
                     .filter(key => {
                       const sec = formData.sectionsDict[key];
                       return sec && !isSummarySection(sec);
                     })
                     .map((sectionKey) => renderSection(sectionKey, isSectionTreeCompact))
                   }
                 </div>
             </div>
         </div>
         
         <div className="mt-0 text-end" >
           <button 
             type="submit" 
             className="ev-btn-blue-fill-round ev-bold-reg" 
             disabled={isLoading || !formData.fabric_id}
           >
             <i className="bi bi-cursor-fill"></i> {editingTemplate ? 'Update Template' : 'Create Template'}
           </button>
           <button 
             type="button" 
             className="ev-btn-blue-outline-round ev-bold-reg ms-2" 
             onClick={onCancel}
           >
             Cancel
           </button>
         </div>
       </form>
         {/* Value Message Configuration Modal */}
         {valueMessageModalOpen && (
         <ConfigureVM
           initialConfig={vmConfiguration}
           fabricTreeData={fabricTreeData}
           onSave={handleSaveVmConfiguration}
           onClose={closeValueMessageModal}
         />
       )}

        {/* Magic Prompt Confirmation Modal */}
        <ConfirmationModal
          show={showMagicConfirm}
          onClose={() => setShowMagicConfirm(false)}
          onConfirm={handleConfirmMagicGenerate}
          title="Confirm Magic Template Generation"
          message=""
          confirmButtonText="Generate & Replace"
          confirmButtonClass="btn-warning"
          bodyContent={getMagicConfirmBodyContent()}
        />

        <ConfirmationModal
        show={showImportConfirm}
        onClose={() => setShowImportConfirm(false)}
        onConfirm={handleConfirmImport}
        title="Confirm Template Import"
        message="" // Message is handled by bodyContent
        confirmButtonText="Import & Replace"
        confirmButtonClass="btn-warning"
        bodyContent={getImportConfirmBodyContent()}
      />

      <ConfirmationModal
        show={showFabricSwitchConfirm}
        onClose={handleCancelFabricSwitch}
        onConfirm={handleConfirmFabricSwitch}
        title="Discard Generated Template?"
        message=""
        confirmButtonText="Discard & Switch"
        confirmButtonClass="btn-warning"
        cancelButtonText="Keep Current Template"
        bodyContent={getFabricSwitchConfirmBodyContent()}
      />

      {agentConfigureOpen && (
      <AgentConfigure
        isOpen={agentConfigureOpen}
        onClose={closeAgentConfigure}
        formData={formData}
        sectionKey={agentToConfigure.sectionKey}
        agentKey={agentToConfigure.agentKey}
        fabricTreeData={fabricTreeData}
        updateAgentField={updateAgentField}
      />
    )}
      {showReportFormatting && (
    <ReportFormattingModal
      onClose={closeReportFormatting}
      onSave={handleReportFormattingSave}
      templateId={editingTemplate?.id}
      initialConfig={formData.format_json}
    />
  )}

      {/* Special Section Info — shown only on first-ever conversion */}
      <ConfirmationModal
        show={showSpecialSectionInfo}
        onClose={() => setShowSpecialSectionInfo(false)}
        onConfirm={() => setShowSpecialSectionInfo(false)}
        title="Special Sections"
        confirmButtonText="Got it"
        confirmButtonClass="btn-primary"
        showCancel={false}
        bodyContent={
          <div className="text-center">
            <div className="special-info-container mb-3">
              <div className="special-info-doc-wrapper">
                {/* Before state */}
                <div className="special-info-state">
                  <span className="special-info-state-label">Before</span>
                  <div className="special-info-doc">
                    <div className="special-info-item special-info-item-toc">
                      <i className="bi bi-list-ul me-1"></i>Table of Contents
                    </div>
                    <div className="special-info-item special-info-item-sec">1. Section A</div>
                    <div className="special-info-item special-info-item-target">
                      2. Section B
                    </div>
                    <div className="special-info-item special-info-item-sec">3. Section C</div>
                  </div>
                </div>

                {/* Arrow */}
                <div className="special-info-arrow-center">
                  <i className="bi bi-arrow-right-short"></i>
                </div>

                {/* After state */}
                <div className="special-info-state">
                  <span className="special-info-state-label">After</span>
                  <div className="special-info-doc">
                    <div className="special-info-item special-info-item-special special-info-item-glow">
                      <i className="bi bi-star-fill me-1"></i>S1. Section B
                    </div>
                    <div className="special-info-item special-info-item-toc">
                      <i className="bi bi-list-ul me-1"></i>Table of Contents
                    </div>
                    <div className="special-info-item special-info-item-sec">1. Section A</div>
                    <div className="special-info-item special-info-item-sec">2. Section C</div>
                  </div>
                </div>
              </div>
            </div>
            <p className="ev-fs-13 mb-2">
              <strong>Special sections are priority sections</strong> that render at the very beginning of your document — <em>before</em> the Table of Contents.
            </p>
            <p className="ev-fs-12 text-muted mb-0">
              Toggle any root section to make it special. It moves to the top automatically with all its subsections and agents. Toggle again to move it back.
            </p>
          </div>
        }
      />

    </div>
   );
 });

 GenbiTemplateForm.displayName = 'GenbiTemplateForm';
 export default GenbiTemplateForm;